import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  School, Users, UserPlus, Plus, Copy, RefreshCw, Search, Pencil, Image,
  Building2, GraduationCap, ChevronDown, ChevronUp, Upload, X, Eye, KeyRound, Lightbulb, MessageSquare, CheckCircle2, Download, FileSpreadsheet, Phone, ClipboardList, ShieldCheck, AlertTriangle, UserCog, History, CalendarCheck, Briefcase, Wrench, LifeBuoy, HeartHandshake, Trash2,
} from 'lucide-react';
import GoogleCalendarCard from './admin/GoogleCalendarCard';
import InternshipCareerQueue from './admin/InternshipCareerQueue';
import CloneDemoSchoolDialog from './admin/CloneDemoSchoolDialog';
import SchoolManageDialog from './admin/SchoolManageDialog';
import SchoolIdMaintenanceDialog from './admin/SchoolIdMaintenanceDialog';
import TicketsTab from './admin/TicketsTab';
import UsersTab from './admin/UsersTab';
import SchoolInterestTab from './admin/SchoolInterestTab';
import { EDUCATION_BOARDS, BOARDS_NEEDING_DETAIL, boardLabel } from '@/data/educationBoards';
import ReportIssueButton from '@/components/ReportIssueButton';
import { PARTICIPATION_AGREEMENT, SAFEGUARDING_AGREEMENT } from '@/data/agreements';
import { AgreementModal } from '@/components/AgreementModal';
import { toast } from 'sonner';
import { nameError, emailError, mobileError, textError, selectError, apiFieldError } from '@/lib/formValidation';
import { FIELD_CLASS, EMAIL_PROPS, TEL_PROPS } from '@/lib/mobileForm';

const FieldHint = ({ error }) => (
  error ? <p className="text-[11px] text-red-600 mt-1" data-testid="field-error">{error}</p> : null
);

const API_BASE = '';

const PREFIX_STOP_WORDS = ['the', 'of', 'and', 'school', 'schools', 'international', 'public', 'high'];

/** Mirrors the backend's suggestion: initials of the meaningful words in the name. */
const suggestPrefix = (name) => {
  const words = (name || '').match(/[A-Za-z]+/g) || [];
  const meaningful = words.filter(w => !PREFIX_STOP_WORDS.includes(w.toLowerCase()));
  const pool = meaningful.length ? meaningful : words;
  const initials = pool.map(w => w[0]).join('').slice(0, 3).toUpperCase();
  if (initials.length === 3) return initials;
  return ((pool[0] || 'SCH').toUpperCase() + 'SCH').slice(0, 3);
};

// ─── Stats Overview ───
function StatsBar({ stats }) {
  if (!stats) return null;
  const items = [
    { label: 'Schools', value: stats.schools, icon: <School className="h-5 w-5 text-[#F97D6A]" /> },
    { label: 'Total Users', value: stats.users, icon: <Users className="h-5 w-5 text-[#F97D6A]" /> },
    { label: 'Parents', value: stats.parents, icon: <Users className="h-5 w-5 text-blue-500" /> },
    { label: 'Coordinators', value: stats.coordinators, icon: <UserPlus className="h-5 w-5 text-green-500" /> },
    { label: 'Children', value: stats.children, icon: <Users className="h-5 w-5 text-purple-500" /> },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6" data-testid="admin-stats">
      {items.map((s) => (
        <div key={s.label} className="bg-white rounded-2xl border border-gray-200 p-4" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#F5F6F8] flex items-center justify-center">{s.icon}</div>
            <div>
              <p className="text-xl font-bold text-[#1F2937] font-['Outfit']">{s.value}</p>
              <p className="text-[11px] text-[#9CA3AF] font-medium">{s.label}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Schools Tab ───
const SCHOOL_TYPE_STYLES = {
  DEMO: 'bg-amber-50 text-amber-700 hover:bg-amber-100',
  PILOT: 'bg-blue-50 text-blue-700 hover:bg-blue-100',
  LIVE: 'bg-green-50 text-green-700 hover:bg-green-100',
};

const SCHOOL_TYPE_COPY = {
  DEMO: 'Nothing reaches the school. Every message is redirected to the monitoring inbox.',
  PILOT: 'Families receive their email, and a copy of each one reaches the monitoring inbox.',
  LIVE: 'Families receive their email. No monitoring copies are kept.',
};

function SchoolTypeDialog({ school, onClose, onDone }) {
  const current = school.email_mode || school.school_type || 'DEMO';
  const category = school.school_category
    || ({ DM: 'DEMO', PL: 'PILOT', SC: 'LIVE' }[(school.school_id || '').slice(0, 2)] || 'DEMO');
  const allowed = { DEMO: ['DEMO'], PILOT: ['DEMO', 'PILOT'], LIVE: ['DEMO', 'PILOT', 'LIVE'] }[category];
  const [choice, setChoice] = useState(current);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [readiness, setReadiness] = useState(null);
  const leavingDemo = current === 'DEMO' && choice !== 'DEMO';

  useEffect(() => {
    api.get(`/admin/schools/${school.school_id}/overview`)
      .then(res => setReadiness(res.data.readiness))
      .catch(() => setReadiness(null));
  }, [school.school_id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/schools/${school.school_id}/type`, {
        school_type: choice, confirm: true, note,
      });
      toast.success(`${school.school_name} is now ${choice}`);
      onDone();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail
        : detail?.message || 'Could not change the email mode');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-2xl sm:max-w-lg" data-testid="school-type-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit']">Email mode &middot; {school.school_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          {['DEMO', 'PILOT', 'LIVE'].map(t => (
            <button key={t} onClick={() => allowed.includes(t) && setChoice(t)}
              disabled={!allowed.includes(t)}
              className={`w-full text-left px-4 py-3 rounded-xl border transition-colors ${choice === t ? 'border-[#F97D6A] bg-[#FFF8F7]' : 'border-gray-200 hover:border-gray-300'} ${allowed.includes(t) ? '' : 'opacity-40 cursor-not-allowed'}`}
              data-testid={`school-type-option-${t}`}>
              <p className="text-sm font-bold text-[#1F2937]">{t}</p>
              <p className="text-xs text-[#6B7280] mt-0.5">
                {allowed.includes(t) ? SCHOOL_TYPE_COPY[t]
                  : `Only a ${t === 'LIVE' ? 'live' : 'pilot or live'} school can deliver this way.`}
              </p>
            </button>
          ))}
          <p className="text-[11px] text-[#9CA3AF]" data-testid="school-type-category-note">
            This is a <span className="font-semibold">{category}</span> school. Its category and its
            {' '}{school.school_id} number never change &mdash; only how its email is delivered.
          </p>
        </div>
        {leavingDemo && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 space-y-1" data-testid="school-type-warning">
            <p className="text-xs text-amber-800">
              Real people at {school.school_name} will start receiving email from CareersPOV as soon
              as you save this. Make sure their addresses are the ones you expect.
            </p>
            {(readiness?.warnings || []).map(w => (
              <p key={w} className="text-xs text-amber-800" data-testid="school-type-readiness">• {w}</p>
            ))}
            {readiness?.fictional_accounts > 0 && (
              <p className="text-[11px] text-amber-700">
                Demo history is kept as it is — nothing is deleted and no fictional account becomes a
                real user.
              </p>
            )}
          </div>
        )}
        <Textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
          placeholder="Why is this changing? (kept on the school's record)"
          className="rounded-xl text-sm" data-testid="school-type-note" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl">Cancel</Button>
          <Button onClick={save} disabled={saving || choice === current}
            className="bg-[#1F2937] text-white hover:bg-[#111827] rounded-xl font-semibold"
            data-testid="confirm-school-type">
            {saving ? 'Saving...' : leavingDemo ? `Yes, switch to ${choice}` : `Save as ${choice}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SchoolsTab({ schools, onRefresh, allCoordinators, onViewUsers }) {
  const [typeTarget, setTypeTarget] = useState(null);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [manageTarget, setManageTarget] = useState(null);
  const [maintenanceOpen, setMaintenanceOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editSchool, setEditSchool] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ school_name: '', ticket_prefix: '' });
  const [prefixTouched, setPrefixTouched] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [toggling, setToggling] = useState(null);

  const handleSchoolNameChange = (val) => {
    const updates = { school_name: val };
    if (!editForm.certificate_footer_name || editForm.certificate_footer_name === editSchool?.school_name) {
      updates.certificate_footer_name = val;
    }
    setEditForm(prev => ({ ...prev, ...updates }));
  };

  const toggleSchoolStatus = async (school) => {
    const newStatus = isActive(school) ? 'inactive' : 'active';
    // For deactivation, require confirmation dialog first
    if (newStatus === 'inactive') {
      setDeactivateTarget(school);
      return;
    }
    await doToggle(school.school_id, newStatus);
  };

  const confirmDeactivate = async () => {
    if (!deactivateTarget) return;
    await doToggle(deactivateTarget.school_id, 'inactive');
    setDeactivateTarget(null);
  };

  const doToggle = async (schoolId, newStatus) => {
    setToggling(schoolId);
    try {
      await api.put(`/admin/schools/${schoolId}`, { status: newStatus });
      onRefresh();
      toast.success(`School ${newStatus === 'active' ? 'reactivated' : 'deactivated'} successfully`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    } finally { setToggling(null); }
  };

  const categoryOf = (s) => s.school_category
    || ({ DM: 'DEMO', PL: 'PILOT', SC: 'LIVE' }[(s.school_id || '').slice(0, 2)] || 'DEMO');

  const isActive = (s) => (s.status || 'active').toUpperCase() === 'ACTIVE';
  const filtered = schools.filter(s => {
    const matchSearch = s.school_name.toLowerCase().includes(search.toLowerCase()) ||
      s.school_id.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all'
      || (['DEMO', 'PILOT', 'LIVE'].includes(statusFilter)
        ? categoryOf(s) === statusFilter
        : (s.status || '').toLowerCase() === statusFilter);
    return matchSearch && matchStatus;
  });

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(''), 2000);
  };

  const createSchool = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await api.post('/schools', createForm);
      setCreateOpen(false);
      setCreateForm({ school_name: '', ticket_prefix: '' });
      setPrefixTouched(false);
      onRefresh();
      toast.success('School created');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed');
    }
    setLoading(false);
  };

  const openEdit = (school) => {
    setEditSchool(school);
    setEditForm({
      school_name: school.school_name || '',
      address_line_1: school.address_line_1 || '',
      address_line_2: school.address_line_2 || '',
      city: school.city || '',
      state: school.state || '',
      country: school.country || '',
      postal_code: school.postal_code || '',
      school_email: school.school_email || '',
      school_phone: school.school_phone || '',
      principal_name: school.principal_name || '',
      certificate_footer_name: school.certificate_footer_name || school.school_name || '',
      ticket_prefix: school.ticket_prefix || '',
      status: school.status || 'active',
      safe_name: school.safeguarding?.name || '',
      safe_designation: school.safeguarding?.designation || '',
      safe_email: school.safeguarding?.email || '',
      safe_mobile: school.safeguarding?.mobile || '',
      auth_rep_name: school.authorized_representative?.name || '',
      auth_rep_designation: school.authorized_representative?.designation || '',
      education_board: school.education_board || '',
      education_board_detail: school.education_board_detail || '',
    });
    setError('');
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const payload = {
        school_name: editForm.school_name,
        address_line_1: editForm.address_line_1,
        address_line_2: editForm.address_line_2,
        city: editForm.city,
        state: editForm.state,
        country: editForm.country,
        postal_code: editForm.postal_code,
        school_email: editForm.school_email,
        school_phone: editForm.school_phone,
        principal_name: editForm.principal_name,
        certificate_footer_name: editForm.certificate_footer_name,
        ticket_prefix: editForm.ticket_prefix,
        status: editForm.status,
        education_board: editForm.education_board,
        education_board_detail: editForm.education_board_detail,
        safeguarding: { name: editForm.safe_name, designation: editForm.safe_designation, email: editForm.safe_email, mobile: editForm.safe_mobile },
        authorized_representative: { name: editForm.auth_rep_name, designation: editForm.auth_rep_designation },
      };
      await api.put(`/admin/schools/${editSchool.school_id}`, payload);
      setEditSchool(null);
      onRefresh();
      toast.success('School updated');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed');
    }
    setLoading(false);
  };

  const handleLogoUpload = async (schoolId, file) => {
    setUploadingLogo(schoolId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/admin/schools/${schoolId}/upload-logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onRefresh();
      toast.success('Logo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    setUploadingLogo(null);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search schools..."
            className="pl-9 rounded-xl h-10 border-gray-200"
            data-testid="school-search-input"
          />
        </div>
        <Button onClick={() => setMaintenanceOpen(true)} variant="ghost"
                className="rounded-xl text-sm font-semibold text-[#6B7280] hover:bg-gray-100"
                title="Check that every school is on its permanent category number"
                data-testid="school-id-maintenance-btn">
          <Wrench className="h-4 w-4 mr-1" /> School ids
        </Button>
        <Button onClick={() => setCloneOpen(true)} variant="outline" className="rounded-xl text-sm font-semibold border-[#F97D6A] text-[#F97D6A] hover:bg-[#FFF0ED]" data-testid="clone-demo-school-btn">
          <Copy className="h-4 w-4 mr-1" /> New Pilot School
        </Button>
        <Button onClick={() => { setCreateOpen(true); setError(''); }} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl text-sm font-semibold" data-testid="add-school-btn">
          <Plus className="h-4 w-4 mr-1" /> Add School
        </Button>
      </div>

      <div className="flex items-center gap-2 mb-4 overflow-x-auto" data-testid="schools-status-filter">
        {[
          { id: 'all', label: `All (${schools.length})` },
          { id: 'DEMO', label: `Demo schools (${schools.filter(s=>categoryOf(s)==='DEMO').length})` },
          { id: 'PILOT', label: `Pilot schools (${schools.filter(s=>categoryOf(s)==='PILOT').length})` },
          { id: 'LIVE', label: `Live schools (${schools.filter(s=>categoryOf(s)==='LIVE').length})` },
          { id: 'closed', label: `Closed (${schools.filter(s=>(s.status||'').toLowerCase()==='closed').length})` },
        ].map(t => (
          <button key={t.id} onClick={() => setStatusFilter(t.id)} className={`text-[11px] font-semibold px-3 py-1.5 rounded-lg whitespace-nowrap transition-colors ${statusFilter === t.id ? 'bg-[#F97D6A] text-white' : 'bg-white text-[#6B7280] hover:bg-gray-50 border border-gray-200'}`} data-testid={`school-filter-${t.id}`}>{t.label}</button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="schools-table">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Logo</th>
                <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">School</th>
                <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Code</th>
                <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">City</th>
                <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Board</th>
                <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Invite</th>
                <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Coordinators</th>
                <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Category</th>
                <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Email mode</th>
                <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Status</th>
                <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} className="border-b border-gray-50 hover:bg-gray-50/40" data-testid={`school-row-${s.school_id}`}>
                  <td className="py-3 px-4">
                    <div className="relative w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden group">
                      {s.school_logo_url ? (
                        <img src={`${API_BASE}${s.school_logo_url}`} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Building2 className="h-5 w-5 text-gray-400" />
                      )}
                      <label className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center cursor-pointer transition-opacity">
                        <Upload className="h-4 w-4 text-white" />
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && handleLogoUpload(s.school_id, e.target.files[0])} />
                      </label>
                      {uploadingLogo === s.school_id && <div className="absolute inset-0 bg-white/70 flex items-center justify-center"><RefreshCw className="h-4 w-4 animate-spin text-[#F97D6A]" /></div>}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <p className="font-semibold text-[#1F2937]">{s.school_name}</p>
                    {s.principal_name && <p className="text-xs text-gray-400">Principal: {s.principal_name}</p>}
                  </td>
                  <td className="py-3 px-4"><Badge className="bg-[#FFF0ED] text-[#F97D6A] border-0 text-[10px]">{s.school_id}</Badge></td>
                  <td className="py-3 px-4 text-gray-500">{s.city || '—'}</td>
                  <td className="py-3 px-4 text-gray-500 whitespace-nowrap" data-testid={`school-board-${s.school_id}`}>
                    {boardLabel(s)}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-1.5">
                      <code className="text-xs font-mono bg-gray-50 px-1.5 py-0.5 rounded border border-gray-200">{s.invite_code}</code>
                      <button onClick={() => copyCode(s.invite_code)} className="text-gray-400 hover:text-[#F97D6A]" data-testid={`copy-invite-${s.school_id}`}>
                        <Copy className="h-3 w-3" />
                      </button>
                      {copied === s.invite_code && <span className="text-[10px] text-green-500">Copied</span>}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    {(s.coordinators || []).length > 0 ? (
                      <div className="space-y-0.5">
                        {s.coordinators.map(c => (
                          <p key={c.id} className="text-xs text-gray-600">{c.full_name}</p>
                        ))}
                      </div>
                    ) : <span className="text-xs text-gray-400">None</span>}
                  </td>
                  <td className="py-3 px-4">
                    <Badge className={`border-0 text-[10px] font-bold tracking-wide ${
                      { DEMO: 'bg-slate-100 text-slate-600',
                        PILOT: 'bg-indigo-50 text-indigo-600',
                        LIVE: 'bg-emerald-50 text-emerald-700' }[categoryOf(s)]}`}
                           data-testid={`school-category-${s.school_id}`}>
                      {categoryOf(s)}
                    </Badge>
                  </td>
                  <td className="py-3 px-4">
                    <button
                      onClick={() => setTypeTarget(s)}
                      className={`px-2 py-1 rounded-lg text-[10px] font-bold tracking-wide transition-colors ${SCHOOL_TYPE_STYLES[s.email_mode || s.school_type] || SCHOOL_TYPE_STYLES.DEMO}`}
                      title="Change how this school's email is delivered"
                      data-testid={`school-type-${s.school_id}`}
                    >
                      {s.email_mode || s.school_type || 'DEMO'}
                    </button>
                  </td>
                  <td className="py-3 px-4">
                    <Badge className={`border-0 text-[10px] ${
                      isActive(s) ? 'bg-green-50 text-green-600'
                        : (s.status || '').toUpperCase() === 'CLOSED' ? 'bg-amber-50 text-amber-700'
                        : 'bg-red-50 text-red-600'}`}
                           data-testid={`school-status-${s.school_id}`}>
                      {isActive(s) ? 'Active'
                        : (s.status || '').toUpperCase() === 'CLOSED' ? 'Closed' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-1.5">
                      <Button variant="ghost" size="sm" onClick={() => setManageTarget(s)} className="h-7 px-2 text-[10px] font-semibold rounded-lg text-[#F97D6A] hover:bg-[#FFF0ED]" data-testid={`manage-school-${s.school_id}`}>
                        Manage
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(s)} className="h-8 w-8 p-0" data-testid={`edit-school-${s.school_id}`}>
                        <Pencil className="h-3.5 w-3.5 text-gray-500" />
                      </Button>
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => toggleSchoolStatus(s)}
                        disabled={toggling === s.school_id}
                        className={`h-7 px-2 text-[10px] font-semibold rounded-lg ${isActive(s) ? 'text-red-500 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}`}
                        data-testid={`toggle-status-${s.school_id}`}
                      >
                        {toggling === s.school_id ? '...' : isActive(s) ? 'Deactivate' : 'Reactivate'}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && <p className="text-center text-sm text-gray-400 py-8">No schools found</p>}
        {typeTarget && <SchoolTypeDialog school={typeTarget} onClose={() => setTypeTarget(null)}
          onDone={() => { setTypeTarget(null); onRefresh(); }} />}
        {cloneOpen && <CloneDemoSchoolDialog onClose={() => setCloneOpen(false)}
          onDone={() => onRefresh()} />}
        {manageTarget && <SchoolManageDialog school={manageTarget}
          onClose={() => setManageTarget(null)} onRefresh={onRefresh}
          onChangeType={(s) => { setManageTarget(null); setTypeTarget(s); }}
          onViewUsers={(sid) => { setManageTarget(null); onViewUsers?.(sid); }} />}
        {maintenanceOpen && <SchoolIdMaintenanceDialog onClose={() => setMaintenanceOpen(false)}
          onDone={() => onRefresh()} />}
      </div>

      {/* Create School Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-2xl" data-testid="create-school-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Create New School</DialogTitle>
            <DialogDescription>Enter the school name. ID and invite code will be generated automatically.</DialogDescription>
          </DialogHeader>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>}
          <form onSubmit={createSchool} className="space-y-4">
            <div>
              <Label className="text-[#1F2937] font-medium">School Name</Label>
              <Input value={createForm.school_name} onChange={(e) => {
                const school_name = e.target.value;
                setCreateForm(prev => ({
                  ...prev, school_name,
                  ticket_prefix: prefixTouched ? prev.ticket_prefix : suggestPrefix(school_name),
                }));
              }} placeholder="e.g. Orchid International School" required className="mt-1 rounded-xl h-10 border-gray-200" data-testid="school-name-input" />
            </div>
            <div>
              <Label className="text-[#1F2937] font-medium">Ticket Prefix</Label>
              <Input value={createForm.ticket_prefix} maxLength={3}
                     onChange={(e) => { setPrefixTouched(true); setCreateForm({ ...createForm, ticket_prefix: e.target.value.toUpperCase() }); }}
                     placeholder="ORC" className="mt-1 rounded-xl h-10 border-gray-200 w-28 font-mono tracking-widest"
                     data-testid="school-ticket-prefix-input" />
              <p className="text-[10px] text-gray-400 mt-1">Three letters used on this school's ticket numbers, e.g. {(createForm.ticket_prefix || 'ORC')}0001. Editable until the school's first ticket.</p>
            </div>
            <div className="bg-blue-50 rounded-xl px-4 py-3">
              <p className="text-xs text-blue-600">School ID (e.g. SC0021) and Invite Code will be auto-generated. You can add all other details later via Edit.</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} className="rounded-xl">Cancel</Button>
              <Button type="submit" disabled={loading} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl" data-testid="create-school-submit">{loading ? 'Creating...' : 'Create School'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit School Dialog */}
      <Dialog open={!!editSchool} onOpenChange={(open) => !open && setEditSchool(null)}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="edit-school-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Edit School — {editSchool?.school_name}</DialogTitle>
            <DialogDescription>Update school details, branding, address, and certificate settings</DialogDescription>
          </DialogHeader>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>}

          {/* School Identity (read-only) */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400">School Identity</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-gray-500">School ID</Label>
                <div className="mt-1 flex items-center gap-2">
                  <code className="text-sm font-mono font-bold bg-white px-3 py-1.5 rounded-lg border border-gray-200 text-[#1F2937]" data-testid="edit-school-id-display">{editSchool?.school_id}</code>
                  <button type="button" onClick={() => copyCode(editSchool?.school_id)} className="text-gray-400 hover:text-[#F97D6A] transition-colors" data-testid="copy-school-id">
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                  {copied === editSchool?.school_id && <span className="text-[10px] text-green-500">Copied</span>}
                </div>
                <p className="text-[10px] text-gray-400 mt-0.5">Auto-generated, read-only</p>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Invite Code</Label>
                <div className="mt-1 flex items-center gap-2">
                  <code className="text-sm font-mono font-bold bg-white px-3 py-1.5 rounded-lg border border-gray-200 text-[#1F2937]" data-testid="edit-invite-code-display">{editSchool?.invite_code}</code>
                  <button type="button" onClick={() => copyCode(editSchool?.invite_code)} className="text-gray-400 hover:text-[#F97D6A] transition-colors" data-testid="copy-invite-code-edit">
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                  {copied === editSchool?.invite_code && <span className="text-[10px] text-green-500">Copied</span>}
                </div>
                <p className="text-[10px] text-gray-400 mt-0.5">Used by parents to register</p>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Ticket Prefix</Label>
                <div className="mt-1 flex items-center gap-2">
                  <Input value={editForm.ticket_prefix || ''} maxLength={3}
                         disabled={editSchool?.ticket_prefix_locked}
                         onChange={(e) => setEditForm({ ...editForm, ticket_prefix: e.target.value.toUpperCase() })}
                         className="rounded-lg h-9 w-24 font-mono tracking-widest border-gray-200 disabled:bg-gray-50"
                         data-testid="edit-ticket-prefix-input" />
                  {editSchool?.ticket_prefix_locked && (
                    <span className="text-[10px] text-amber-700 bg-amber-50 rounded-full px-2 py-0.5 font-semibold" data-testid="ticket-prefix-locked">Locked</span>
                  )}
                </div>
                <p className="text-[10px] text-gray-400 mt-0.5">
                  {editSchool?.ticket_prefix_locked
                    ? 'Locked — this school already has tickets'
                    : 'Used on ticket numbers until the first ticket is raised'}
                </p>
              </div>
            </div>
          </div>

          {/* School Logo */}
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">School Logo</p>
            <div className="flex items-center gap-4">
              <div className="relative w-16 h-16 rounded-xl bg-white border border-gray-200 flex items-center justify-center overflow-hidden group">
                {editSchool?.school_logo_url ? (
                  <img src={`${API_BASE}${editSchool.school_logo_url}`} alt="Logo" className="w-full h-full object-cover" />
                ) : (
                  <Building2 className="h-6 w-6 text-gray-300" />
                )}
                <label className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center cursor-pointer transition-opacity">
                  <Upload className="h-4 w-4 text-white" />
                  <input type="file" accept="image/*" className="hidden" data-testid="edit-school-logo-upload" onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const formData = new FormData();
                      formData.append('file', file);
                      const res = await api.post(`/admin/schools/${editSchool?.school_id}/upload-logo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                      toast.success('Logo uploaded');
                      onRefresh();
                      setEditSchool(prev => prev ? {...prev, school_logo_url: res.data.school_logo_url} : null);
                    } catch { toast.error('Upload failed'); }
                  }} />
                </label>
              </div>
              <div>
                <p className="text-sm text-gray-600">Hover and click to upload or replace</p>
                <p className="text-xs text-gray-400">PNG, JPG — used on certificates and reports</p>
              </div>
            </div>
          </div>

          <form onSubmit={saveEdit} noValidate className="space-y-4">
            <div>
              <Label className="text-[#1F2937] font-medium">School Name</Label>
              <Input value={editForm.school_name} onChange={(e) => handleSchoolNameChange(e.target.value)} className="mt-1 rounded-xl h-10 border-gray-200" data-testid="edit-school-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#1F2937] font-medium">Email</Label>
                <Input value={editForm.school_email} onChange={(e) => setEditForm({...editForm, school_email: e.target.value})} placeholder="info@school.com" className="mt-1 rounded-xl h-10 border-gray-200" data-testid="edit-school-email" />
              </div>
              <div>
                <Label className="text-[#1F2937] font-medium">Phone</Label>
                <Input value={editForm.school_phone} onChange={(e) => setEditForm({...editForm, school_phone: e.target.value})} placeholder="+91..." className="mt-1 rounded-xl h-10 border-gray-200" data-testid="edit-school-phone" />
              </div>
            </div>
            <div>
              <Label className="text-[#1F2937] font-medium">Address Line 1</Label>
              <Input value={editForm.address_line_1} onChange={(e) => setEditForm({...editForm, address_line_1: e.target.value})} className="mt-1 rounded-xl h-10 border-gray-200" data-testid="edit-school-addr1" />
            </div>
            <div>
              <Label className="text-[#1F2937] font-medium">Address Line 2</Label>
              <Input value={editForm.address_line_2} onChange={(e) => setEditForm({...editForm, address_line_2: e.target.value})} className="mt-1 rounded-xl h-10 border-gray-200" data-testid="edit-school-addr2" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-[#1F2937] font-medium">City</Label>
                <Input value={editForm.city} onChange={(e) => setEditForm({...editForm, city: e.target.value})} className="mt-1 rounded-xl h-10 border-gray-200" data-testid="edit-school-city" />
              </div>
              <div>
                <Label className="text-[#1F2937] font-medium">State</Label>
                <Input value={editForm.state} onChange={(e) => setEditForm({...editForm, state: e.target.value})} className="mt-1 rounded-xl h-10 border-gray-200" data-testid="edit-school-state" />
              </div>
              <div>
                <Label className="text-[#1F2937] font-medium">Postal Code</Label>
                <Input value={editForm.postal_code} onChange={(e) => setEditForm({...editForm, postal_code: e.target.value})} className="mt-1 rounded-xl h-10 border-gray-200" data-testid="edit-school-postal" />
              </div>
            </div>
            <div>
              <Label className="text-[#1F2937] font-medium">Country</Label>
              <Input value={editForm.country} onChange={(e) => setEditForm({...editForm, country: e.target.value})} className="mt-1 rounded-xl h-10 border-gray-200" data-testid="edit-school-country" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[#1F2937] font-medium">Education Board</Label>
                <select
                  value={editForm.education_board}
                  onChange={(e) => setEditForm({ ...editForm, education_board: e.target.value, education_board_detail: '' })}
                  className="mt-1 w-full rounded-xl h-10 border border-gray-200 px-3 text-sm text-[#1F2937] bg-white focus:outline-none focus:ring-2 focus:ring-[#F97D6A]/40"
                  data-testid="edit-school-board"
                >
                  <option value="">Select board</option>
                  {EDUCATION_BOARDS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              {BOARDS_NEEDING_DETAIL.includes(editForm.education_board) && (
                <div>
                  <Label className="text-[#1F2937] font-medium">
                    {editForm.education_board === 'State Board' ? 'Which state board' : 'Which board'}
                  </Label>
                  <Input value={editForm.education_board_detail}
                         onChange={(e) => setEditForm({ ...editForm, education_board_detail: e.target.value })}
                         placeholder={editForm.education_board === 'State Board' ? 'e.g. Maharashtra State Board' : 'Name of the board'}
                         className="mt-1 rounded-xl h-10 border-gray-200" data-testid="edit-school-board-detail" />
                </div>
              )}
            </div>
            <div>
              <Label className="text-[#1F2937] font-medium">Principal Name</Label>
              <Input value={editForm.principal_name} onChange={(e) => setEditForm({...editForm, principal_name: e.target.value})} className="mt-1 rounded-xl h-10 border-gray-200" data-testid="edit-school-principal" />
            </div>
            <div className="bg-[#FFF0ED] rounded-xl p-4">
              <Label className="text-[#F97D6A] font-semibold text-xs uppercase tracking-wider">Certificate Settings</Label>
              <div className="mt-2">
                <Label className="text-[#1F2937] font-medium text-sm">Certificate Footer Name</Label>
                <Input value={editForm.certificate_footer_name} onChange={(e) => setEditForm({...editForm, certificate_footer_name: e.target.value})} placeholder="Name shown on certificates" className="mt-1 rounded-xl h-10 border-gray-200" data-testid="edit-school-cert-footer" />
                <p className="text-xs text-gray-400 mt-1">Appears as the school's name on student certificates</p>
              </div>
            </div>
            {/* Primary Coordinator — read-only, managed via Coordinators tab */}
            {editSchool?.coordinator && (
              <div className="bg-blue-50/60 rounded-xl p-4" data-testid="edit-school-coordinator-info">
                <p className="text-xs font-bold uppercase tracking-wider text-blue-400 mb-2">Primary Coordinator</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  <div><span className="text-gray-400">Name:</span> <span className="text-[#1F2937]">{editSchool.coordinator.name}</span></div>
                  <div><span className="text-gray-400">Designation:</span> <span className="text-[#1F2937]">{editSchool.coordinator.designation}</span></div>
                  <div><span className="text-gray-400">Email:</span> <span className="text-[#1F2937]">{editSchool.coordinator.email}</span></div>
                  <div><span className="text-gray-400">Mobile:</span> <span className="text-[#1F2937]">{editSchool.coordinator.mobile}</span></div>
                </div>
                <p className="text-[10px] text-blue-400 mt-2">Manage coordinators from the Coordinators tab</p>
              </div>
            )}

            {/* Safeguarding Contact — read-only, managed via Coordinators tab */}
            {editSchool?.safeguarding && (
              <div className="bg-emerald-50/60 rounded-xl p-4" data-testid="edit-school-safeguarding-info">
                <p className="text-xs font-bold uppercase tracking-wider text-emerald-400 mb-2">Safeguarding Contact</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  <div><span className="text-gray-400">Name:</span> <span className="text-[#1F2937]">{editSchool.safeguarding.name}</span></div>
                  <div><span className="text-gray-400">Designation:</span> <span className="text-[#1F2937]">{editSchool.safeguarding.designation}</span></div>
                  <div><span className="text-gray-400">Email:</span> <span className="text-[#1F2937]">{editSchool.safeguarding.email}</span></div>
                  <div><span className="text-gray-400">Mobile:</span> <span className="text-[#1F2937]">{editSchool.safeguarding.mobile}</span></div>
                </div>
                <p className="text-[10px] text-emerald-400 mt-2">Manage coordinators from the Coordinators tab</p>
              </div>
            )}

            {/* Authorized Representative — editable */}
            <div className="bg-violet-50/60 rounded-xl p-4 space-y-3" data-testid="edit-school-auth-rep-info">
              <p className="text-xs font-bold uppercase tracking-wider text-violet-400">Authorized Representative</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-500">Name</Label>
                  <Input value={editForm.auth_rep_name} onChange={(e) => setEditForm({...editForm, auth_rep_name: e.target.value})} className="mt-1 rounded-lg h-9 border-gray-200 text-sm" data-testid="edit-auth-rep-name" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Designation</Label>
                  <Input value={editForm.auth_rep_designation} onChange={(e) => setEditForm({...editForm, auth_rep_designation: e.target.value})} className="mt-1 rounded-lg h-9 border-gray-200 text-sm" data-testid="edit-auth-rep-designation" />
                </div>
              </div>
            </div>

            {/* Agreements — read-only */}
            {editSchool?.agreements && (
              <div className="bg-amber-50/60 rounded-xl p-4" data-testid="edit-school-agreements-info">
                <p className="text-xs font-bold uppercase tracking-wider text-amber-500 mb-2">Agreements (read-only)</p>
                <div className="space-y-1.5">
                  {['participation', 'safeguarding'].map((key) => {
                    const a = editSchool.agreements?.[key];
                    if (!a) return null;
                    return (
                      <div key={key} className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                        <span className="text-[#1F2937] capitalize">{key === 'participation' ? 'School Participation Agreement' : 'Safeguarding Responsibilities'}</span>
                        <span className="text-[9px] bg-amber-100 text-amber-600 rounded px-1.5 py-0.5 ml-1">{a.version}</span>
                        <span className="text-[#9CA3AF] text-xs ml-auto">{a.accepted_at ? new Date(a.accepted_at).toLocaleString() : ''}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <Label className="text-[#1F2937] font-medium">Status</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm({...editForm, status: v})}>
                <SelectTrigger className="mt-1 rounded-xl h-10 border-gray-200" data-testid="edit-school-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditSchool(null)} className="rounded-xl">Cancel</Button>
              <Button type="submit" disabled={loading} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl" data-testid="save-school-btn">{loading ? 'Saving...' : 'Save Changes'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Deactivation Confirmation Dialog */}
      <Dialog open={!!deactivateTarget} onOpenChange={(o) => { if (!o) setDeactivateTarget(null); }}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-red-600">Deactivate School</DialogTitle>
            <DialogDescription>
              Are you sure you want to deactivate <span className="font-semibold text-[#1F2937]">{deactivateTarget?.school_name}</span>?
            </DialogDescription>
          </DialogHeader>
          <div className="bg-red-50 rounded-xl p-4 text-sm text-red-800 space-y-1.5 my-2">
            <p>This will immediately:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Suspend all coordinators, parents and students associated with this school</li>
              <li>Block new registrations using its invite code</li>
              <li>Prevent new bookings, sessions and activities</li>
            </ul>
            <p className="mt-2">All records, agreements and history will be preserved. You can reactivate the school later.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeactivateTarget(null)} className="rounded-xl">Cancel</Button>
            <Button onClick={confirmDeactivate} disabled={toggling === deactivateTarget?.school_id} className="bg-red-600 hover:bg-red-700 text-white rounded-xl" data-testid="confirm-deactivate-btn">
              {toggling === deactivateTarget?.school_id ? 'Deactivating...' : 'Deactivate School'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Coordinators Tab ───
function CoordinatorsTab({ coordinators, schools, onRefresh }) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editCoord, setEditCoord] = useState(null);
  const [safeTarget, setSafeTarget] = useState(null);   // { coord, granted }
  const [safeReason, setSafeReason] = useState('');
  const [editForm, setEditForm] = useState({});
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ full_name: '', email: '', mobile: '', school_id: '', coordinator_type: 'primary', designation: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const setCreateField = (key, value) => {
    setCreateForm(prev => ({ ...prev, [key]: value }));
    setFieldErrors(prev => (prev[key] ? { ...prev, [key]: '' } : prev));
  };

  const filtered = coordinators.filter(c => {
    const matchSearch = c.full_name.toLowerCase().includes(search.toLowerCase()) ||
      c.email.toLowerCase().includes(search.toLowerCase()) ||
      c.school_name.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || (statusFilter === 'inactive' ? !['active','pending_activation'].includes(c.status) : c.status === statusFilter);
    return matchSearch && matchStatus;
  });

  const createCoordinator = async (e) => {
    e.preventDefault();
    setError('');
    const next = {
      full_name: nameError(createForm.full_name, 'Full name'),
      email: emailError(createForm.email, 'Email'),
      mobile: mobileError(createForm.mobile, 'Mobile'),
      designation: textError(createForm.designation, 'Designation', { required: false }),
      school_id: selectError(createForm.school_id, 'School'),
    };
    setFieldErrors(next);
    if (Object.values(next).some(Boolean)) return;
    setLoading(true);
    try {
      await api.post('/coordinators', createForm);
      setCreateOpen(false);
      setCreateForm({ full_name: '', email: '', mobile: '', school_id: '', coordinator_type: 'primary', designation: '' });
      setFieldErrors({});
      onRefresh();
      toast.success('Coordinator created with Pending Activation status. Send them an activation link.');
    } catch (err) {
      const { field, message } = apiFieldError(err, 'Failed');
      if (field) setFieldErrors(prev => ({ ...prev, [field]: message }));
      else setError(message);
    }
    setLoading(false);
  };

  const openEdit = (coord) => {
    setEditCoord(coord);
    setEditForm({
      full_name: coord.full_name || '',
      email: coord.email || '',
      mobile: coord.mobile || '',
      designation: coord.designation || '',
      school_id: coord.school_id || '',
      status: coord.status || 'active',
    });
    setError('');
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    const next = {
      full_name: nameError(editForm.full_name, 'Full name'),
      email: emailError(editForm.email, 'Email'),
      mobile: mobileError(editForm.mobile, 'Mobile', { required: false }),
    };
    setFieldErrors(next);
    if (Object.values(next).some(Boolean)) return;
    setLoading(true);
    setError('');
    try {
      await api.put(`/admin/coordinators/${editCoord.id}`, editForm);
      setEditCoord(null);
      setFieldErrors({});
      onRefresh();
      toast.success('Coordinator updated');
    } catch (err) {
      const { field, message } = apiFieldError(err, 'Failed');
      if (field) setFieldErrors(prev => ({ ...prev, [field]: message }));
      else setError(message);
    }
    setLoading(false);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search coordinators..." className="pl-9 rounded-xl h-10 border-gray-200" data-testid="coordinator-search-input" />
        </div>
        <Button onClick={() => { setCreateOpen(true); setError(''); }} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl text-sm font-semibold" data-testid="add-coordinator-btn">
          <UserPlus className="h-4 w-4 mr-1" /> Add Coordinator
        </Button>
      </div>

      <div className="flex items-center gap-2 mb-4 overflow-x-auto" data-testid="coord-status-filter">
        {[
          { id: 'all', label: `All (${coordinators.length})` },
          { id: 'active', label: `Active (${coordinators.filter(c=>c.status==='active').length})` },
          { id: 'pending_activation', label: `Pending (${coordinators.filter(c=>c.status==='pending_activation').length})` },
          { id: 'inactive', label: `Inactive (${coordinators.filter(c=>!['active','pending_activation'].includes(c.status)).length})` },
        ].map(t => (
          <button key={t.id} onClick={() => setStatusFilter(t.id)} className={`text-[11px] font-semibold px-3 py-1.5 rounded-lg whitespace-nowrap transition-colors ${statusFilter === t.id ? 'bg-[#F97D6A] text-white' : 'bg-white text-[#6B7280] hover:bg-gray-50 border border-gray-200'}`} data-testid={`coord-filter-${t.id}`}>{t.label}</button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-gray-200">
        <div className="overflow-x-auto rounded-2xl">
          <table className="w-full min-w-[1080px] text-sm" data-testid="coordinators-table">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Name</th>
                <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Email</th>
                <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Phone</th>
                <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Designation</th>
                <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Role</th>
                <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Safeguarding</th>
                <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">School</th>
                <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Status</th>
                <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} className="border-b border-gray-50 hover:bg-gray-50/40" data-testid={`coord-row-${c.id}`}>
                  <td className="py-3 px-4 font-medium text-[#1F2937]">{c.full_name}</td>
                  <td className="py-3 px-4 text-gray-500">{c.email}</td>
                  <td className="py-3 px-4 text-gray-500">{c.mobile || '—'}</td>
                  <td className="py-3 px-4 text-gray-500">{c.designation || '—'}</td>
                  <td className="py-3 px-4">
                    {c.coordinator_type === 'coordinator' || c.coordinator_type === 'primary' ? (
                      <Badge className="bg-blue-50 text-blue-600 border-0 text-[10px]">Primary</Badge>
                    ) : c.coordinator_type === 'backup' ? (
                      <Badge className="bg-gray-50 text-gray-500 border-0 text-[10px]">Backup</Badge>
                    ) : (
                      <Badge className="bg-gray-50 text-gray-500 border-0 text-[10px]">Coordinator</Badge>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      {c.safeguarding_access ? (
                        <Badge className="bg-emerald-50 text-emerald-700 border-0 text-[10px]" data-testid={`safeguarding-badge-${c.id}`}>
                          <ShieldCheck className="h-3 w-3 mr-1" />Authorised
                        </Badge>
                      ) : (
                        <span className="text-[11px] text-gray-400" data-testid={`safeguarding-badge-${c.id}`}>No access</span>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 px-2.5 text-[11px] font-semibold rounded-lg border-gray-200 text-[#4B5563] hover:border-[#F97D6A] hover:text-[#F97D6A]"
                        onClick={() => { setSafeReason(''); setSafeTarget({ coord: c, granted: !c.safeguarding_access }); }}
                        data-testid={`toggle-safeguarding-${c.id}`}
                      >
                        {c.safeguarding_access ? 'Revoke' : 'Grant'}
                      </Button>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <Badge className="bg-blue-50 text-blue-600 border-0 text-[10px]">{c.school_name || c.school_id}</Badge>
                  </td>
                  <td className="py-3 px-4">
                    <Badge className={`border-0 text-[10px] ${c.status === 'active' ? 'bg-green-50 text-green-600' : c.status === 'pending_activation' ? 'bg-amber-50 text-amber-600' : 'bg-gray-100 text-gray-500'}`}>{c.status === 'pending_activation' ? 'Pending Activation' : c.status}</Badge>
                  </td>
                  <td className="py-3 px-4 flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(c)} className="h-8 w-8 p-0" data-testid={`edit-coord-${c.id}`}>
                      <Pencil className="h-3.5 w-3.5 text-gray-500" />
                    </Button>
                    {c.status === 'pending_activation' ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-auto px-2 py-1 text-[11px] text-amber-600 hover:bg-amber-50 font-medium"
                        title={c.activation_sent_at ? 'Resend activation link' : 'Send activation link'}
                        data-testid={`activate-coord-${c.id}`}
                        onClick={async () => {
                          try {
                            await api.post('/admin/send-activation', { user_id: c.id });
                            toast.success(`Activation link sent to ${c.email}`);
                            onRefresh();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Failed to send activation link');
                          }
                        }}
                      >
                        {c.activation_sent_at ? 'Resend' : 'Send'} Activation
                      </Button>
                    ) : c.status === 'active' ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        title="Send password reset link"
                        data-testid={`reset-coord-${c.id}`}
                        onClick={async () => {
                          try {
                            await api.post('/admin/reset-user-password', { user_id: c.id });
                            toast.success(`Reset link sent to ${c.email}`);
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Failed to send reset link');
                          }
                        }}
                      >
                        <KeyRound className="h-3.5 w-3.5 text-gray-500" />
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && <p className="text-center text-sm text-gray-400 py-8">No coordinators found</p>}
      </div>

      {/* Safeguarding permission */}
      <Dialog open={!!safeTarget} onOpenChange={(open) => !open && setSafeTarget(null)}>
        <DialogContent className="rounded-2xl" data-testid="safeguarding-access-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">
              {safeTarget?.granted ? 'Grant Safeguarding permission' : 'Revoke Safeguarding permission'}
            </DialogTitle>
            <DialogDescription>
              {safeTarget?.granted
                ? `${safeTarget?.coord?.full_name} will see the Safeguarding section for ${safeTarget?.coord?.school_name || safeTarget?.coord?.school_id} inside their Career Coordinator Dashboard. Their coordinator access is unchanged.`
                : `${safeTarget?.coord?.full_name} will lose access to the Safeguarding section immediately. Their coordinator access is unchanged.`}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Reason (optional)</Label>
            <Textarea value={safeReason} onChange={(e) => setSafeReason(e.target.value)} rows={2}
                      placeholder="Recorded in the account history"
                      className="mt-1 rounded-xl border-gray-200 text-sm" data-testid="safeguarding-reason-input" />
          </div>
          <div className="bg-amber-50 rounded-xl px-4 py-3 text-xs text-amber-700">
            Platform Admin controls who holds this permission but cannot read a school's confidential
            safeguarding reports.
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setSafeTarget(null)} className="rounded-xl h-11 sm:h-10">Cancel</Button>
            <Button
              disabled={loading}
              className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl h-11 sm:h-10"
              data-testid="confirm-safeguarding-access"
              onClick={async () => {
                setLoading(true);
                try {
                  const res = await api.post(`/admin/coordinators/${safeTarget.coord.id}/safeguarding-access`,
                    { granted: safeTarget.granted, reason: safeReason });
                  toast.success(res.data.message);
                  setSafeTarget(null);
                  onRefresh();
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Could not change the permission');
                }
                setLoading(false);
              }}
            >
              {loading ? 'Saving…' : safeTarget?.granted ? 'Grant permission' : 'Revoke permission'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Create Coordinator Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-2xl" data-testid="create-coordinator-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Add Coordinator</DialogTitle>
            <DialogDescription>Assign a coordinator to a school</DialogDescription>
          </DialogHeader>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>}
          <form onSubmit={createCoordinator} noValidate className="space-y-4">
            <div>
              <Label>Full Name</Label>
              <Input value={createForm.full_name} onChange={(e) => setCreateField('full_name', e.target.value)} className={`mt-1 rounded-xl ${FIELD_CLASS} ${fieldErrors.full_name ? 'border-red-400' : 'border-gray-200'}`} data-testid="coord-name-input" />
              <FieldHint error={fieldErrors.full_name} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email</Label>
                <Input {...EMAIL_PROPS} value={createForm.email} onChange={(e) => setCreateField('email', e.target.value)} className={`mt-1 rounded-xl ${FIELD_CLASS} ${fieldErrors.email ? 'border-red-400' : 'border-gray-200'}`} data-testid="coord-email-input" />
                <FieldHint error={fieldErrors.email} />
              </div>
              <div>
                <Label>Mobile</Label>
                <Input {...TEL_PROPS} value={createForm.mobile} onChange={(e) => setCreateField('mobile', e.target.value)} placeholder="+919876543210" className={`mt-1 rounded-xl ${FIELD_CLASS} ${fieldErrors.mobile ? 'border-red-400' : 'border-gray-200'}`} data-testid="coord-mobile-input" />
                <FieldHint error={fieldErrors.mobile} />
              </div>
            </div>
            <div>
              <Label>Designation</Label>
              <Input value={createForm.designation} onChange={(e) => setCreateField('designation', e.target.value)} placeholder="e.g. Vice Principal" className={`mt-1 rounded-xl ${FIELD_CLASS} ${fieldErrors.designation ? 'border-red-400' : 'border-gray-200'}`} data-testid="coord-designation-input" />
              <FieldHint error={fieldErrors.designation} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>School</Label>
                <Select value={createForm.school_id} onValueChange={(v) => setCreateField('school_id', v)}>
                  <SelectTrigger className={`mt-1 rounded-xl h-11 sm:h-10 text-base sm:text-sm ${fieldErrors.school_id ? 'border-red-400' : 'border-gray-200'}`} data-testid="coord-school-select"><SelectValue placeholder="Select school" /></SelectTrigger>
                  <SelectContent>
                    {schools.map((s) => <SelectItem key={s.school_id} value={s.school_id}>{s.school_name} ({s.school_id})</SelectItem>)}
                  </SelectContent>
                </Select>
                <FieldHint error={fieldErrors.school_id} />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={createForm.coordinator_type} onValueChange={(v) => setCreateField('coordinator_type', v)}>
                  <SelectTrigger className="mt-1 rounded-xl h-11 sm:h-10 text-base sm:text-sm border-gray-200" data-testid="coord-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="primary">Primary</SelectItem>
                    <SelectItem value="safeguarding">Safeguarding</SelectItem>
                    <SelectItem value="backup">Backup</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="bg-blue-50 rounded-xl px-4 py-3 text-sm text-blue-700">
              The coordinator will be created with Pending Activation status. Use "Send Activation" to email them a link to set their password.
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} className="rounded-xl h-11 sm:h-10">Cancel</Button>
              <Button type="submit" disabled={loading} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl h-11 sm:h-10" data-testid="create-coordinator-submit">{loading ? 'Creating...' : 'Add Coordinator'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Coordinator Dialog */}
      <Dialog open={!!editCoord} onOpenChange={(open) => !open && setEditCoord(null)}>
        <DialogContent className="rounded-2xl" data-testid="edit-coordinator-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Edit Coordinator</DialogTitle>
            <DialogDescription>Update coordinator details</DialogDescription>
          </DialogHeader>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>}
          <form onSubmit={saveEdit} noValidate className="space-y-4">
            <div>
              <Label>Full Name</Label>
              <Input value={editForm.full_name} onChange={(e) => { setEditForm({...editForm, full_name: e.target.value}); setFieldErrors(p => ({ ...p, full_name: '' })); }} className={`mt-1 rounded-xl ${FIELD_CLASS} ${fieldErrors.full_name ? 'border-red-400' : 'border-gray-200'}`} data-testid="edit-coord-name" />
              <FieldHint error={fieldErrors.full_name} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email</Label>
                <Input {...EMAIL_PROPS} value={editForm.email} onChange={(e) => { setEditForm({...editForm, email: e.target.value}); setFieldErrors(p => ({ ...p, email: '' })); }} className={`mt-1 rounded-xl ${FIELD_CLASS} ${fieldErrors.email ? 'border-red-400' : 'border-gray-200'}`} data-testid="edit-coord-email" />
                <FieldHint error={fieldErrors.email} />
              </div>
              <div>
                <Label>Mobile</Label>
                <Input {...TEL_PROPS} value={editForm.mobile} onChange={(e) => { setEditForm({...editForm, mobile: e.target.value}); setFieldErrors(p => ({ ...p, mobile: '' })); }} className={`mt-1 rounded-xl ${FIELD_CLASS} ${fieldErrors.mobile ? 'border-red-400' : 'border-gray-200'}`} data-testid="edit-coord-mobile" />
                <FieldHint error={fieldErrors.mobile} />
              </div>
            </div>
            <div>
              <Label>Designation</Label>
              <Input value={editForm.designation} onChange={(e) => setEditForm({...editForm, designation: e.target.value})} placeholder="e.g. Career Coordinator" className="mt-1 rounded-xl h-10 border-gray-200" data-testid="edit-coord-designation" />
            </div>
            <div>
              <Label>Assigned School</Label>
              <Select value={editForm.school_id} onValueChange={(v) => setEditForm({...editForm, school_id: v})}>
                <SelectTrigger className="mt-1 rounded-xl h-10 border-gray-200" data-testid="edit-coord-school"><SelectValue placeholder="Select school" /></SelectTrigger>
                <SelectContent>
                  {schools.map((s) => <SelectItem key={s.school_id} value={s.school_id}>{s.school_name} ({s.school_id})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm({...editForm, status: v})}>
                <SelectTrigger className="mt-1 rounded-xl h-10 border-gray-200" data-testid="edit-coord-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditCoord(null)} className="rounded-xl">Cancel</Button>
              <Button type="submit" disabled={loading} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl" data-testid="save-coord-btn">{loading ? 'Saving...' : 'Save Changes'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Careers Tab ───
function CareersTab({ careers, onRefresh }) {
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editCareer, setEditCareer] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadType, setUploadType] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [deleteCareer, setDeleteCareer] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [previewImg, setPreviewImg] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importMode, setImportMode] = useState('all');
  const [importPreview, setImportPreview] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const importFileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get('/admin/careers/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Career_Import_Template.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch { toast.error('Failed to download template'); }
  };

  const handleExportCareers = async () => {
    try {
      const res = await api.get('/admin/careers/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Careers_Export.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Careers exported');
    } catch { toast.error('Failed to export careers'); }
  };

  const handleImportPreview = async () => {
    if (!importFile) return;
    setImportLoading(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const res = await api.post('/admin/careers/import-preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportPreview(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to preview'); }
    setImportLoading(false);
  };

  const handleImportConfirm = async () => {
    if (!importFile) return;
    setImportLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      fd.append('mode', importMode);
      const res = await api.post('/admin/careers/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult(res.data);
      setImportPreview(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    setImportLoading(false);
  };

  const resetImport = () => { setImportOpen(false); setImportFile(null); setImportPreview(null); setImportResult(null); setImportMode('all'); };

  const categories = [...new Set(careers.map(c => c.category).filter(Boolean))].sort();

  const isDraft = (c) => !c.is_active && !c.short_description;

  const filtered = careers.filter(c => {
    const matchSearch = c.name.toLowerCase().includes(search.toLowerCase()) || c.category.toLowerCase().includes(search.toLowerCase());
    const matchCat = catFilter === 'all' || c.category === catFilter;
    const matchStatus = statusFilter === 'all'
      || (statusFilter === 'active' && c.is_active)
      || (statusFilter === 'inactive' && !c.is_active && !isDraft(c))
      || (statusFilter === 'draft' && isDraft(c));
    return matchSearch && matchCat && matchStatus;
  });

  const openEdit = (career) => {
    setEditCareer(career);
    setEditForm({
      name: career.name || '',
      category: career.category || '',
      short_description: career.short_description || '',
      explanation: career.explanation || '',
      day_in_life: career.day_in_life || '',
      recommended_subjects: (career.recommended_subjects || career.required_subjects || career.subjects || []).join(', '),
      helpful_subjects: (career.helpful_subjects || []).join(', '),
      admission_subjects: (career.admission_subjects || []).join(', '),
      preparation_style: career.preparation_style || '',
      best_fit_for: career.best_fit_for || '',
      work_environment: career.work_environment || '',
      path_after_school: career.path_after_school || '',
      mentor_session_focus: career.mentor_session_focus || '',
      real_world_example: career.real_world_example || '',
      stream: career.stream || '',
      is_active: career.is_active !== false,
    });
    setError('');
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const payload = {
        ...editForm,
        recommended_subjects: editForm.recommended_subjects.split(',').map(s => s.trim()).filter(Boolean),
        helpful_subjects: editForm.helpful_subjects.split(',').map(s => s.trim()).filter(Boolean),
        admission_subjects: editForm.admission_subjects.split(',').map(s => s.trim()).filter(Boolean),
      };
      await api.put(`/admin/careers/${editCareer.id}`, payload);
      setEditCareer(null);
      onRefresh();
      toast.success('Career updated');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed');
    }
    setLoading(false);
  };

  const toggleActive = async (career) => {
    try {
      await api.put(`/admin/careers/${career.id}`, { is_active: !career.is_active });
      onRefresh();
      toast.success(career.is_active ? 'Career deactivated' : 'Career activated');
    } catch (err) {
      toast.error('Failed to toggle');
    }
  };

  const openDelete = async (career) => {
    setDeleteCareer({ career, loading: true, references: [], deletable: false });
    try {
      const res = await api.get(`/admin/careers/${career.id}/references`);
      setDeleteCareer({ career, loading: false, ...res.data });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not check this career');
      setDeleteCareer(null);
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      const res = await api.delete(`/admin/careers/${deleteCareer.career.id}`);
      setDeleteCareer(null);
      onRefresh();
      toast.success(`${res.data.name} deleted permanently`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete this career');
    }
    setDeleting(false);
  };

  const uploadImage = async (careerId, imageType, file) => {
    setUploadingId(careerId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('image_type', imageType);
      await api.post(`/admin/careers/${careerId}/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onRefresh();
      toast.success(`${imageType} image uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    setUploadingId(null);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search careers..." className="pl-9 rounded-xl h-10 border-gray-200" data-testid="career-search-input" />
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-48 rounded-xl h-10 border-gray-200" data-testid="career-cat-filter">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={handleDownloadTemplate} className="rounded-xl text-sm font-semibold border-gray-200 gap-1.5 shrink-0" data-testid="download-template-btn">
          <Download className="h-4 w-4" /> Template
        </Button>
        <Button variant="outline" onClick={handleExportCareers} className="rounded-xl text-sm font-semibold border-gray-200 gap-1.5 shrink-0" data-testid="export-careers-btn">
          <Download className="h-4 w-4" /> Export
        </Button>
        <Button onClick={() => { resetImport(); setImportOpen(true); }} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl text-sm font-semibold gap-1.5 shrink-0" data-testid="import-careers-btn">
          <FileSpreadsheet className="h-4 w-4" /> Import
        </Button>
      </div>

      <div className="flex items-center gap-2 mb-3 overflow-x-auto" data-testid="careers-status-filter">
        {[
          { id: 'all', label: `All (${careers.length})` },
          { id: 'active', label: `Active (${careers.filter(c=>c.is_active).length})` },
          { id: 'inactive', label: `Inactive (${careers.filter(c=>!c.is_active && !isDraft(c)).length})` },
          { id: 'draft', label: `Draft (${careers.filter(c=>isDraft(c)).length})` },
        ].map(t => (
          <button key={t.id} onClick={() => setStatusFilter(t.id)} className={`text-[11px] font-semibold px-3 py-1.5 rounded-lg whitespace-nowrap transition-colors ${statusFilter === t.id ? 'bg-[#F97D6A] text-white' : 'bg-white text-[#6B7280] hover:bg-gray-50 border border-gray-200'}`} data-testid={`career-filter-${t.id}`}>{t.label}</button>
        ))}
      </div>

      <div className="text-xs text-gray-400 mb-2">{filtered.length} career{filtered.length !== 1 ? 's' : ''} shown</div>

      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="careers-table">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Images</th>
                <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Career</th>
                <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Category</th>
                <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Stream</th>
                <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Active</th>
                <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const cardImg = c.image_card_url || c.image_url;
                const heroImg = c.image_hero_url || c.image_url;
                return (
                  <tr key={c.id} className={`border-b border-gray-50 hover:bg-gray-50/40 ${!c.is_active ? 'opacity-50' : ''}`} data-testid={`career-row-${c.slug}`}>
                    <td className="py-3 px-4">
                      <div className="flex gap-1.5">
                        <div className="relative w-10 h-12 rounded-md bg-gray-100 overflow-hidden group cursor-pointer" title="Card image" onClick={() => setPreviewImg(cardImg?.startsWith('/api') ? `${API_BASE}${cardImg}` : cardImg)}>
                          {cardImg ? (
                            <img src={cardImg.startsWith('/api') ? `${API_BASE}${cardImg}` : cardImg} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Image className="h-4 w-4 text-gray-300 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                          )}
                          <label className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center cursor-pointer transition-opacity">
                            <Upload className="h-3 w-3 text-white" />
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadImage(c.id, 'card', e.target.files[0])} />
                          </label>
                        </div>
                        <div className="relative w-14 h-12 rounded-md bg-gray-100 overflow-hidden group cursor-pointer" title="Hero image" onClick={() => setPreviewImg(heroImg?.startsWith('/api') ? `${API_BASE}${heroImg}` : heroImg)}>
                          {heroImg ? (
                            <img src={heroImg.startsWith('/api') ? `${API_BASE}${heroImg}` : heroImg} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Image className="h-4 w-4 text-gray-300 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                          )}
                          <label className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center cursor-pointer transition-opacity">
                            <Upload className="h-3 w-3 text-white" />
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadImage(c.id, 'hero', e.target.files[0])} />
                          </label>
                        </div>
                        {uploadingId === c.id && <RefreshCw className="h-4 w-4 animate-spin text-[#F97D6A] self-center" />}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <p className="font-semibold text-[#1F2937]">{c.name}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <p className="text-xs text-gray-400 truncate max-w-[180px]">{c.short_description || (c.is_active ? '' : 'Draft — needs content')}</p>
                        {!c.is_active && <Badge className="bg-amber-50 text-amber-600 border-0 text-[9px] shrink-0">Draft</Badge>}
                      </div>
                    </td>
                    <td className="py-3 px-4"><Badge className="bg-blue-50 text-blue-600 border-0 text-[10px]">{c.category}</Badge></td>
                    <td className="py-3 px-4"><Badge className={`border-0 text-[10px] ${c.stream === 'Science' ? 'bg-sky-50 text-sky-600' : c.stream === 'Commerce' ? 'bg-amber-50 text-amber-600' : c.stream === 'Arts' ? 'bg-rose-50 text-rose-600' : c.stream === 'Any Stream' ? 'bg-violet-50 text-violet-600' : 'bg-gray-50 text-gray-500'}`}>{c.stream || '—'}</Badge></td>
                    <td className="py-3 px-4">
                      <Switch checked={c.is_active} onCheckedChange={() => toggleActive(c)} data-testid={`toggle-career-${c.slug}`} />
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(c)} className="h-8 w-8 p-0" data-testid={`edit-career-${c.slug}`}>
                          <Pencil className="h-3.5 w-3.5 text-gray-500" />
                        </Button>
                        {!c.is_active && (
                          <Button variant="ghost" size="sm" onClick={() => openDelete(c)} className="h-8 w-8 p-0 hover:bg-red-50" data-testid={`delete-career-${c.slug}`}>
                            <Trash2 className="h-3.5 w-3.5 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && <p className="text-center text-sm text-gray-400 py-8">No careers found</p>}
      </div>

      {/* Delete Career Dialog — inactive careers only, and only when nothing points at them */}
      <Dialog open={!!deleteCareer} onOpenChange={(open) => !open && setDeleteCareer(null)}>
        <DialogContent className="rounded-2xl max-w-md" data-testid="delete-career-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">
              Delete {deleteCareer?.career?.name}?
            </DialogTitle>
            <DialogDescription className="text-xs">
              {deleteCareer?.loading
                ? 'Checking where this career is used…'
                : deleteCareer?.deletable
                  ? 'Nothing references this career. Deleting removes the career record permanently — this cannot be undone. Its images stop being used, though the stored files themselves can only be cleared by a storage cleanup.'
                  : 'This career cannot be deleted.'}
            </DialogDescription>
          </DialogHeader>

          {!deleteCareer?.loading && deleteCareer?.references?.length > 0 && (
            <div className="rounded-xl bg-amber-50 border border-amber-100 px-3 py-2.5"
                 data-testid="delete-career-references">
              <p className="text-xs font-semibold text-amber-800 mb-1.5">Still in use by:</p>
              <ul className="space-y-0.5">
                {deleteCareer.references.map((r) => (
                  <li key={r.label} className="text-xs text-amber-700">
                    {r.count} {r.label.toLowerCase()}
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-amber-700 mt-2">
                It stays inactive so nothing already recorded breaks.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteCareer(null)} className="rounded-xl"
                    data-testid="delete-career-cancel">
              {deleteCareer?.deletable ? 'Cancel' : 'Close'}
            </Button>
            {deleteCareer?.deletable && (
              <Button onClick={confirmDelete} disabled={deleting}
                      className="bg-red-600 text-white hover:bg-red-700 rounded-xl"
                      data-testid="delete-career-confirm">
                {deleting ? 'Deleting…' : 'Delete permanently'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog open={!!previewImg} onOpenChange={(open) => !open && setPreviewImg(null)}>        <DialogContent className="rounded-2xl max-w-lg p-2">
          {previewImg && <img src={previewImg} alt="Preview" className="w-full rounded-xl" />}
        </DialogContent>
      </Dialog>

      {/* Edit Career Dialog */}
      <Dialog open={!!editCareer} onOpenChange={(open) => !open && setEditCareer(null)}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="edit-career-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Edit Career — {editCareer?.name}</DialogTitle>
            <DialogDescription>Update career content and settings</DialogDescription>
          </DialogHeader>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>}
          <form onSubmit={saveEdit} noValidate className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">Career Name</Label>
                <Input value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} className="mt-1 rounded-xl h-10 border-gray-200" data-testid="edit-career-name" />
              </div>
              <div>
                <Label className="text-sm font-medium">Category</Label>
                <Input value={editForm.category} onChange={(e) => setEditForm({...editForm, category: e.target.value})} className="mt-1 rounded-xl h-10 border-gray-200" data-testid="edit-career-category" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">Stream</Label>
                <Select value={editForm.stream || 'none'} onValueChange={(v) => setEditForm({...editForm, stream: v === 'none' ? '' : v})}>
                  <SelectTrigger className="mt-1 rounded-xl h-10 border-gray-200" data-testid="edit-career-stream"><SelectValue placeholder="Select stream" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not set</SelectItem>
                    <SelectItem value="Science">Science</SelectItem>
                    <SelectItem value="Commerce">Commerce</SelectItem>
                    <SelectItem value="Arts">Arts</SelectItem>
                    <SelectItem value="Any Stream">Any Stream</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium">Preparation Style</Label>
                <Input value={editForm.preparation_style} onChange={(e) => setEditForm({...editForm, preparation_style: e.target.value})} className="mt-1 rounded-xl h-10 border-gray-200" data-testid="edit-career-prep" />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">Short Description</Label>
              <Textarea value={editForm.short_description} onChange={(e) => setEditForm({...editForm, short_description: e.target.value})} rows={2} className="mt-1 rounded-xl border-gray-200" data-testid="edit-career-short-desc" />
            </div>
            <div>
              <Label className="text-sm font-medium">Full Overview</Label>
              <Textarea value={editForm.explanation} onChange={(e) => setEditForm({...editForm, explanation: e.target.value})} rows={3} className="mt-1 rounded-xl border-gray-200" data-testid="edit-career-explanation" />
            </div>
            <div>
              <Label className="text-sm font-medium">Typical Day in a Life</Label>
              <Textarea value={editForm.day_in_life} onChange={(e) => setEditForm({...editForm, day_in_life: e.target.value})} rows={2} className="mt-1 rounded-xl border-gray-200" data-testid="edit-career-dayinlife" />
            </div>
            <div>
              <Label className="text-sm font-medium">Admission Subjects (comma-separated)</Label>
              <Input value={editForm.admission_subjects} onChange={(e) => setEditForm({...editForm, admission_subjects: e.target.value})} placeholder="Physics, Chemistry, Biology" className="mt-1 rounded-xl h-10 border-gray-200" data-testid="edit-career-admission-subjects" />
            </div>
            <div>
              <Label className="text-sm font-medium">Recommended Subjects (comma-separated)</Label>
              <Input value={editForm.recommended_subjects} onChange={(e) => setEditForm({...editForm, recommended_subjects: e.target.value})} placeholder="Physics, Chemistry, Mathematics" className="mt-1 rounded-xl h-10 border-gray-200" data-testid="edit-career-recommended-subjects" />
            </div>
            <div>
              <Label className="text-sm font-medium">Helpful Subjects (comma-separated)</Label>
              <Input value={editForm.helpful_subjects} onChange={(e) => setEditForm({...editForm, helpful_subjects: e.target.value})} placeholder="Computer Science, Economics" className="mt-1 rounded-xl h-10 border-gray-200" data-testid="edit-career-helpful-subjects" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">Best Fit For</Label>
                <Input value={editForm.best_fit_for} onChange={(e) => setEditForm({...editForm, best_fit_for: e.target.value})} className="mt-1 rounded-xl h-10 border-gray-200" data-testid="edit-career-bestfit" />
              </div>
              <div>
                <Label className="text-sm font-medium">Work Environment</Label>
                <Input value={editForm.work_environment} onChange={(e) => setEditForm({...editForm, work_environment: e.target.value})} className="mt-1 rounded-xl h-10 border-gray-200" data-testid="edit-career-workenv" />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">Path After School</Label>
              <Textarea value={editForm.path_after_school} onChange={(e) => setEditForm({...editForm, path_after_school: e.target.value})} rows={2} className="mt-1 rounded-xl border-gray-200" data-testid="edit-career-path" />
            </div>
            <div>
              <Label className="text-sm font-medium">Suggested Mentor Session Focus</Label>
              <Textarea value={editForm.mentor_session_focus} onChange={(e) => setEditForm({...editForm, mentor_session_focus: e.target.value})} rows={2} className="mt-1 rounded-xl border-gray-200" data-testid="edit-career-mentorfocus" />
            </div>
            <div>
              <Label className="text-sm font-medium">Real-world Examples</Label>
              <Input value={editForm.real_world_example} onChange={(e) => setEditForm({...editForm, real_world_example: e.target.value})} className="mt-1 rounded-xl h-10 border-gray-200" data-testid="edit-career-examples" />
            </div>
            <div className="flex items-end gap-3 pb-1">
                <Label className="text-sm font-medium">Active</Label>
                <Switch checked={editForm.is_active} onCheckedChange={(v) => setEditForm({...editForm, is_active: v})} data-testid="edit-career-active" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditCareer(null)} className="rounded-xl">Cancel</Button>
              <Button type="submit" disabled={loading} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl" data-testid="save-career-btn">{loading ? 'Saving...' : 'Save Changes'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Careers Dialog */}
      <Dialog open={importOpen} onOpenChange={(open) => { if (!open) resetImport(); }}>
        <DialogContent className="rounded-2xl max-w-lg" data-testid="import-careers-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-[#F97D6A]" /> Import Careers
            </DialogTitle>
            <DialogDescription>Upload an Excel file to create or update careers in bulk.</DialogDescription>
          </DialogHeader>

          {importResult ? (
            <div className="space-y-3" data-testid="import-result">
              <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto">
                <CheckCircle2 className="h-6 w-6 text-emerald-500" />
              </div>
              <h3 className="text-center text-base font-bold text-[#1F2937] font-['Outfit']">Import Complete</h3>
              <div className="bg-gray-50 rounded-xl p-3 space-y-2 text-sm">
                {importResult.draft_updated > 0 && <div className="flex justify-between"><span className="text-[#6B7280]">Draft careers updated</span><span className="font-semibold text-amber-600">{importResult.draft_updated}</span></div>}
                {importResult.existing_updated > 0 && <div className="flex justify-between"><span className="text-[#6B7280]">Existing careers updated</span><span className="font-semibold text-blue-600">{importResult.existing_updated}</span></div>}
                {importResult.new_created > 0 && <div className="flex justify-between"><span className="text-[#6B7280]">New draft careers created</span><span className="font-semibold text-emerald-600">{importResult.new_created}</span></div>}
                {importResult.skipped > 0 && <div className="flex justify-between"><span className="text-[#6B7280]">Skipped</span><span className="font-semibold text-gray-500">{importResult.skipped}</span></div>}
                {importResult.invalid_rows > 0 && <div className="flex justify-between"><span className="text-[#6B7280]">Invalid rows</span><span className="font-semibold text-red-500">{importResult.invalid_rows}</span></div>}
              </div>
              <DialogFooter>
                <Button onClick={resetImport} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl w-full" data-testid="import-done-btn">Done</Button>
              </DialogFooter>
            </div>
          ) : importPreview ? (
            <div className="space-y-3" data-testid="import-preview">
              <h3 className="text-sm font-bold text-[#1F2937]">Preview</h3>
              <div className="bg-gray-50 rounded-xl p-3 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-[#6B7280]">Total valid rows</span><span className="font-semibold">{importPreview.total_rows}</span></div>
                {importPreview.draft_updates > 0 && <div className="flex justify-between"><span className="text-[#6B7280]">Draft careers to update</span><span className="font-semibold text-amber-600">{importPreview.draft_updates}</span></div>}
                {importPreview.existing_updates > 0 && <div className="flex justify-between"><span className="text-[#6B7280]">Existing careers to update</span><span className="font-semibold text-blue-600">{importPreview.existing_updates}</span></div>}
                {importPreview.new_drafts > 0 && <div className="flex justify-between"><span className="text-[#6B7280]">New draft careers to create</span><span className="font-semibold text-emerald-600">{importPreview.new_drafts}</span></div>}
                {importPreview.invalid_rows > 0 && (
                  <div>
                    <div className="flex justify-between mb-1"><span className="text-red-500 font-medium">Invalid rows</span><span className="font-semibold text-red-500">{importPreview.invalid_rows}</span></div>
                    {importPreview.errors.slice(0, 5).map((e, i) => (
                      <p key={i} className="text-[11px] text-red-400 ml-2">Row {e.row}: {e.error}</p>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs font-medium">Import Mode</Label>
                <Select value={importMode} onValueChange={setImportMode}>
                  <SelectTrigger className="mt-1 rounded-xl h-10 border-gray-200 text-sm" data-testid="import-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Run All Safely</SelectItem>
                    <SelectItem value="update_drafts">Update Draft Careers Only</SelectItem>
                    <SelectItem value="create_missing">Create Missing Careers Only</SelectItem>
                    <SelectItem value="update_existing">Update Existing Careers Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => { setImportPreview(null); setImportFile(null); }} className="rounded-xl">Back</Button>
                <Button onClick={handleImportConfirm} disabled={importLoading || importPreview.total_rows === 0} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl" data-testid="confirm-import-btn">
                  {importLoading ? 'Importing...' : `Confirm Import (${importPreview.total_rows} rows)`}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4" data-testid="import-upload">
              <div
                className={`border-2 border-dashed rounded-2xl p-6 text-center transition-colors cursor-pointer ${dragOver ? 'border-[#F97D6A] bg-[#FFF0ED]' : 'border-gray-200 hover:border-[#F97D6A]/40'}`}
                onClick={() => importFileRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
                onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); }}
                onDrop={(e) => {
                  e.preventDefault(); e.stopPropagation(); setDragOver(false);
                  const f = e.dataTransfer.files[0];
                  if (f && f.name.endsWith('.xlsx')) { setImportFile(f); }
                  else { toast.error('Only .xlsx files are accepted'); }
                }}
                data-testid="import-drop-zone"
              >
                <FileSpreadsheet className="h-10 w-10 text-[#D1D5DB] mx-auto mb-3" />
                <p className="text-sm font-medium text-[#1F2937] mb-1">
                  {importFile ? importFile.name : 'Drop Excel file here or click to browse'}
                </p>
                {importFile ? (
                  <p className="text-xs text-emerald-600">{(importFile.size / 1024).toFixed(1)} KB selected</p>
                ) : (
                  <p className="text-xs text-[#9CA3AF]">.xlsx files only</p>
                )}
                <input
                  ref={importFileRef}
                  type="file"
                  accept=".xlsx"
                  className="hidden"
                  onChange={(e) => { if (e.target.files[0]) setImportFile(e.target.files[0]); e.target.value = ''; }}
                  data-testid="import-file-input"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={resetImport} className="rounded-xl">Cancel</Button>
                <Button onClick={handleImportPreview} disabled={!importFile || importLoading} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl" data-testid="preview-import-btn">
                  {importLoading ? 'Analyzing...' : 'Preview Import'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ─── Career Requests Tab ───
const REQ_STATUS_STYLE = {
  new: 'bg-blue-50 text-blue-600',
  reviewed: 'bg-amber-50 text-amber-700',
  approved: 'bg-emerald-50 text-emerald-600',
  rejected: 'bg-red-50 text-red-600',
};

function CareerRequestsTab({ requests, onRefresh }) {
  const [filter, setFilter] = useState('all');
  const [editReq, setEditReq] = useState(null);
  const [editStatus, setEditStatus] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const filtered = filter === 'all' ? requests : requests.filter((r) => r.status === filter);

  const openEdit = (r) => {
    setEditReq(r);
    setEditStatus(r.status);
    setEditNotes(r.admin_notes || '');
  };

  const saveEdit = async () => {
    setLoading(true);
    try {
      await api.put(`/admin/career-requests/${editReq.id}`, { status: editStatus, admin_notes: editNotes });
      toast.success('Request updated');
      setEditReq(null);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
    setLoading(false);
  };

  const formatDate = (d) => {
    if (!d) return '';
    try { const p = d.slice(0,10).split('-'); return `${p[2]}-${p[1]}-${p[0]}`; } catch { return d; }
  };

  return (
    <div data-testid="career-requests-tab">
      <div className="flex items-center gap-2 mb-4 overflow-x-auto">
        {['all', 'new', 'reviewed', 'approved', 'rejected'].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`text-[11px] font-semibold px-3 py-1.5 rounded-lg whitespace-nowrap transition-colors ${
              filter === s ? 'bg-[#F97D6A] text-white' : 'bg-white text-[#6B7280] hover:bg-gray-50 border border-gray-200'
            }`}
            data-testid={`req-filter-${s}`}
          >
            {s === 'all' ? `All (${requests.length})` : `${s.charAt(0).toUpperCase() + s.slice(1)} (${requests.filter((r) => r.status === s).length})`}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center">
          <Lightbulb className="h-8 w-8 text-[#D1D5DB] mx-auto mb-2" />
          <p className="text-sm text-[#9CA3AF]">No career requests found</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="career-requests-table">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Career</th>
                  <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Type</th>
                  <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Requestor</th>
                  <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Email</th>
                  <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Status</th>
                  <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Date</th>
                  <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50/40" data-testid={`req-row-${r.id}`}>
                    <td className="py-3 px-4 font-medium text-[#1F2937]">{r.requested_career_name}</td>
                    <td className="py-3 px-4">
                      <Badge className={`border-0 text-[9px] font-bold uppercase ${r.source === 'mentor' ? 'bg-violet-50 text-violet-600' : 'bg-blue-50 text-blue-600'}`}>
                        {r.source === 'mentor' ? 'Mentor' : 'Student'}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-gray-600">{r.requested_by_name || r.student_name || '—'}</td>
                    <td className="py-3 px-4 text-gray-500 text-xs">{r.email || '—'}</td>
                    <td className="py-3 px-4">
                      <Badge className={`border-0 text-[10px] capitalize ${REQ_STATUS_STYLE[r.status] || 'bg-gray-100 text-gray-500'}`}>{r.status}</Badge>
                    </td>
                    <td className="py-3 px-4 text-gray-500 text-xs whitespace-nowrap">{formatDate(r.created_at)}</td>
                    <td className="py-3 px-4">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(r)} className="h-8 w-8 p-0" data-testid={`edit-req-${r.id}`}>
                        <Pencil className="h-3.5 w-3.5 text-gray-500" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={!!editReq} onOpenChange={(open) => !open && setEditReq(null)}>
        <DialogContent className="rounded-2xl max-w-md" data-testid="edit-request-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Review Career Request</DialogTitle>
            <DialogDescription>{editReq?.requested_career_name}</DialogDescription>
          </DialogHeader>
          {editReq && (
            <div className="space-y-3">
              <div className="bg-gray-50 rounded-xl p-3 text-xs space-y-1.5">
                <div className="flex justify-between"><span className="text-[#9CA3AF]">Type</span><Badge className={`border-0 text-[9px] font-bold uppercase ${editReq.source === 'mentor' ? 'bg-violet-50 text-violet-600' : 'bg-blue-50 text-blue-600'}`}>{editReq.source === 'mentor' ? 'Mentor Request' : 'Student Request'}</Badge></div>
                <div className="flex justify-between"><span className="text-[#9CA3AF]">Requestor</span><span className="text-[#1F2937] font-medium">{editReq.requested_by_name || editReq.student_name || '—'}</span></div>
                {editReq.email && <div className="flex justify-between"><span className="text-[#9CA3AF]">Email</span><span className="text-[#1F2937]">{editReq.email}</span></div>}
                {editReq.category && <div className="flex justify-between"><span className="text-[#9CA3AF]">Category</span><span className="text-[#1F2937]">{editReq.category}</span></div>}
                {editReq.short_description && <div><span className="text-[#9CA3AF] block mb-0.5">Description</span><span className="text-[#4B5563]">{editReq.short_description}</span></div>}
                {editReq.grade && <div className="flex justify-between"><span className="text-[#9CA3AF]">Grade</span><span className="text-[#1F2937]">{editReq.grade}</span></div>}
                {editReq.school_name && <div className="flex justify-between"><span className="text-[#9CA3AF]">School</span><span className="text-[#1F2937]">{editReq.school_name}</span></div>}
                {editReq.why_interested && <div><span className="text-[#9CA3AF] block mb-0.5">Why interested</span><span className="text-[#4B5563]">{editReq.why_interested}</span></div>}
              </div>
              <div>
                <Label className="text-xs font-medium">Status</Label>
                {editReq.status === 'approved' ? (
                  <div className="mt-1 flex items-center gap-2 h-10 px-3 rounded-xl bg-emerald-50 border border-emerald-200">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                    <span className="text-sm font-medium text-emerald-700">Approved (final)</span>
                  </div>
                ) : (
                  <Select value={editStatus} onValueChange={setEditStatus}>
                    <SelectTrigger className="mt-1 rounded-xl h-10 border-gray-200 text-sm" data-testid="req-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="reviewed">Reviewed</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div>
                <Label className="text-xs font-medium">Admin Notes</Label>
                <Textarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)} rows={2} placeholder="Internal notes..." className="mt-1 rounded-xl border-gray-200 text-sm resize-none" data-testid="req-admin-notes" />
              </div>
              {editReq.created_career_slug && (
                <div className="bg-emerald-50 rounded-xl px-3 py-2.5 flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                  <div className="text-xs">
                    <span className="text-emerald-700 font-medium">Draft career created: </span>
                    <span className="text-emerald-600 font-mono">{editReq.created_career_slug}</span>
                    <span className="text-emerald-500 ml-1">— switch to Careers tab to edit & publish</span>
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditReq(null)} className="rounded-xl">Cancel</Button>
                <Button onClick={saveEdit} disabled={loading} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl" data-testid="save-req-btn">{loading ? 'Saving...' : 'Save'}</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ─── Demo Requests Tab (Lead Management) ───
const DEMO_STATUS_STYLE = {
  'New': 'bg-blue-50 text-blue-600',
  'Contacted': 'bg-amber-50 text-amber-700',
  'Demo Scheduled': 'bg-purple-50 text-purple-600',
  'Demo Done': 'bg-cyan-50 text-cyan-700',
  'Converted': 'bg-emerald-50 text-emerald-600',
  'Not Interested': 'bg-gray-100 text-gray-500',
};

const DEMO_STATUS_OPTIONS = ['New', 'Contacted', 'Demo Scheduled', 'Demo Done', 'Converted', 'Not Interested'];

function DemoRequestsTab({ requests, onRefresh }) {
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  const quickFilters = ['All', 'New', 'Contacted', 'Demo Scheduled', 'Converted'];

  const filtered = requests.filter((r) => {
    if (filter !== 'All' && r.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        (r.school_name || '').toLowerCase().includes(q) ||
        (r.contact_name || '').toLowerCase().includes(q) ||
        (r.city || '').toLowerCase().includes(q) ||
        (r.phone || '').includes(q)
      );
    }
    return true;
  });

  const updateStatus = async (id, newStatus) => {
    setStatusLoading(true);
    try {
      await api.put(`/admin/demo-requests/${id}/status`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      onRefresh();
      if (selected && selected.id === id) {
        setSelected({ ...selected, status: newStatus });
      }
    } catch {
      toast.error('Failed to update status');
    } finally {
      setStatusLoading(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try {
      const dt = new Date(d);
      return dt.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return d; }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden" data-testid="demo-requests-tab">
      {/* Header */}
      <div className="p-4 sm:p-5 border-b border-gray-100">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <div className="flex flex-wrap gap-1.5">
            {quickFilters.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  filter === f ? 'bg-[#F97D6A] text-white' : 'bg-gray-100 text-[#4B5563] hover:bg-gray-200'
                }`}
                data-testid={`demo-filter-${f.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {f} {f !== 'All' ? `(${requests.filter(r => r.status === f).length})` : `(${requests.length})`}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search leads..."
              className="pl-9 rounded-xl h-10 border-gray-200 w-full sm:w-56"
              data-testid="demo-search-input"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full" data-testid="demo-requests-table">
          <thead>
            <tr className="border-b border-gray-100 bg-[#FAFAFA]">
              <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">School Name</th>
              <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Contact Name</th>
              <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Phone</th>
              <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">City</th>
              <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Students</th>
              <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Status</th>
              <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Date</th>
              <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={8} className="py-10 text-center text-sm text-[#9CA3AF]">No demo requests found</td></tr>
            ) : (
              filtered.map((r) => (
                <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors" data-testid={`demo-row-${r.id}`}>
                  <td className="py-3 px-4 text-sm font-medium text-[#1F2937]">{r.school_name}</td>
                  <td className="py-3 px-4 text-sm text-[#4B5563]">{r.contact_name}</td>
                  <td className="py-3 px-4">
                    <a href={`tel:${r.phone}`} className="text-sm text-[#F97D6A] font-medium hover:underline" data-testid={`demo-phone-${r.id}`}>{r.phone}</a>
                  </td>
                  <td className="py-3 px-4 text-sm text-[#4B5563]">{r.city}</td>
                  <td className="py-3 px-4 text-sm text-[#4B5563]">{r.num_students || '—'}</td>
                  <td className="py-3 px-4">
                    <Select value={r.status} onValueChange={(v) => updateStatus(r.id, v)}>
                      <SelectTrigger className={`h-8 rounded-lg text-xs font-semibold border-0 w-[140px] ${DEMO_STATUS_STYLE[r.status] || 'bg-gray-100 text-gray-600'}`} data-testid={`demo-status-select-${r.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DEMO_STATUS_OPTIONS.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-3 px-4 text-sm text-[#6B7280]">{formatDate(r.created_at)}</td>
                  <td className="py-3 px-4">
                    <Button variant="ghost" size="sm" onClick={() => setSelected(r)} className="rounded-lg text-xs" data-testid={`demo-view-${r.id}`}>
                      <Eye className="h-3.5 w-3.5 mr-1" /> View
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg rounded-2xl" data-testid="demo-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-lg">Lead Details</DialogTitle>
            <DialogDescription>Full information for this demo request</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'School Name', value: selected.school_name },
                  { label: 'Contact Name', value: selected.contact_name },
                  { label: 'Email', value: selected.email },
                  { label: 'Phone', value: selected.phone, isPhone: true },
                  { label: 'City', value: selected.city },
                  { label: 'School Website', value: selected.school_website, isLink: true },
                  { label: 'Students', value: selected.num_students || '—' },
                  { label: 'Grades', value: selected.grades || '—' },
                  { label: 'Submitted', value: formatDate(selected.created_at) },
                ].map((item) => (
                  <div key={item.label}>
                    <p className="text-xs font-bold uppercase tracking-wider text-[#9CA3AF] mb-1">{item.label}</p>
                    {item.isPhone ? (
                      <a href={`tel:${item.value}`} className="text-sm font-medium text-[#F97D6A] hover:underline" data-testid="demo-detail-phone">{item.value}</a>
                    ) : item.isLink && item.value ? (
                      <a href={item.value} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-[#F97D6A] hover:underline break-all" data-testid="demo-detail-website">{item.value}</a>
                    ) : (
                      <p className="text-sm text-[#1F2937] font-medium">{item.value}</p>
                    )}
                  </div>
                ))}
              </div>

              {selected.message && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-[#9CA3AF] mb-1">Message</p>
                  <p className="text-sm text-[#4B5563] bg-gray-50 rounded-xl p-3">{selected.message}</p>
                </div>
              )}

              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-[#9CA3AF] mb-2">Status</p>
                <Select value={selected.status} onValueChange={(v) => updateStatus(selected.id, v)} disabled={statusLoading}>
                  <SelectTrigger className={`h-10 rounded-xl text-sm font-semibold ${DEMO_STATUS_STYLE[selected.status] || 'bg-gray-100 text-gray-600'}`} data-testid="demo-detail-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DEMO_STATUS_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setSelected(null)} className="rounded-xl">Close</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ─── School Applications Tab ───
function SchoolApplicationsTab({ applications, onRefresh }) {
  const [selected, setSelected] = useState(null);
  const [changesReason, setChangesReason] = useState('');
  const [actionLoading, setActionLoading] = useState('');
  const [viewAgreement, setViewAgreement] = useState(null);

  const STATUS_STYLE = {
    'Pending Review': 'bg-amber-50 text-amber-700',
    'Approved': 'bg-emerald-50 text-emerald-700',
    'Changes Requested': 'bg-orange-50 text-orange-700',
  };

  const handleApprove = async (id) => {
    if (!window.confirm('Approve this application and create the school?')) return;
    setActionLoading('approve');
    try {
      const res = await api.post(`/admin/school-registrations/${id}/approve`);
      toast.success(`${res.data.message} (Invite: ${res.data.invite_code})`);
      setSelected(null);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Approval failed');
    } finally { setActionLoading(''); }
  };

  const handleRequestChanges = async (id) => {
    if (!changesReason.trim()) { toast.error('Please enter a reason'); return; }
    setActionLoading('changes');
    try {
      await api.post(`/admin/school-registrations/${id}/request-changes`, { reason: changesReason });
      toast.success('Changes requested');
      setChangesReason('');
      setSelected(null);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setActionLoading(''); }
  };

  return (
    <div data-testid="school-applications-tab">
      {applications.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center">
          <ClipboardList className="h-10 w-10 text-[#D1D5DB] mx-auto mb-3" />
          <p className="text-sm text-[#9CA3AF]">No school applications yet.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="school-applications-table">
              <thead className="bg-[#F5F6F8] border-b border-gray-100">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">School</th>
                  <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">City</th>
                  <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Coordinator</th>
                  <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Submitted</th>
                  <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Status</th>
                  <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {applications.map((app) => (
                  <tr key={app.id} className="hover:bg-gray-50/50">
                    <td className="py-3 px-4 font-medium text-[#1F2937]">{app.school_name}</td>
                    <td className="py-3 px-4 text-[#6B7280]">{app.school_city}, {app.school_state}</td>
                    <td className="py-3 px-4 text-[#6B7280]">{app.coordinator?.name}</td>
                    <td className="py-3 px-4 text-[#6B7280]">{new Date(app.created_at).toLocaleDateString()}</td>
                    <td className="py-3 px-4">
                      <Badge className={`border-0 text-[10px] ${STATUS_STYLE[app.status] || 'bg-gray-50 text-gray-600'}`}>{app.status}</Badge>
                    </td>
                    <td className="py-3 px-4">
                      <Button variant="ghost" size="sm" onClick={() => { setSelected(app); setChangesReason(''); }} className="text-[#F97D6A] h-7 px-2 text-xs" data-testid={`review-app-${app.id}`}>
                        <Eye className="h-3.5 w-3.5 mr-1" /> Review
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Review Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl">
          {selected && (
            <div>
              <DialogHeader>
                <DialogTitle className="font-['Outfit'] text-lg">{selected.school_name}</DialogTitle>
                <DialogDescription className="sr-only">Review {selected.school_name} application</DialogDescription>
                <div className="flex items-center gap-2 flex-wrap mt-1">
                  <Badge className={`border-0 text-[10px] ${STATUS_STYLE[selected.status] || 'bg-gray-50 text-gray-600'}`}>{selected.status}</Badge>
                  <Badge className={`border-0 text-[10px] ${selected.agreement_status === 'accepted' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                    {selected.agreement_status === 'accepted' ? 'Agreements Accepted V1.0' : 'Pending Agreements'}
                  </Badge>
                  {selected.approved_school_id ? (
                    <Badge className="border-0 text-[10px] bg-green-50 text-green-600">Active: {selected.approved_school_id}</Badge>
                  ) : selected.status === 'Approved' ? (
                    <Badge className="border-0 text-[10px] bg-gray-50 text-gray-500">Not Activated</Badge>
                  ) : null}
                </div>
              </DialogHeader>

              <div className="space-y-5 mt-4">
                {/* School Details */}
                <div>
                  <h4 className="text-xs font-bold text-[#9CA3AF] uppercase tracking-wider mb-2">School Details</h4>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                    <div><span className="text-[#9CA3AF]">Email:</span> <span className="text-[#1F2937]">{selected.school_email}</span></div>
                    <div><span className="text-[#9CA3AF]">Phone:</span> <span className="text-[#1F2937]">{selected.school_phone}</span></div>
                    <div><span className="text-[#9CA3AF]">Address:</span> <span className="text-[#1F2937]">{selected.school_address}</span></div>
                    <div><span className="text-[#9CA3AF]">City:</span> <span className="text-[#1F2937]">{selected.school_city}, {selected.school_state} {selected.school_pin}</span></div>
                    <div><span className="text-[#9CA3AF]">Board:</span> <span className="text-[#1F2937]" data-testid="registration-board">{selected.education_board || '—'}{selected.education_board_detail ? ` · ${selected.education_board_detail}` : ''}</span></div>
                    <div><span className="text-[#9CA3AF]">Principal:</span> <span className="text-[#1F2937]">{selected.principal_name}</span></div>
                  </div>
                </div>

                {/* Contacts */}
                {[
                  { label: 'Primary Coordinator', data: selected.coordinator, icon: 'text-blue-500' },
                  { label: 'Safeguarding Contact', data: selected.safeguarding, icon: 'text-emerald-500' },
                ].map(({ label, data }) => data && (
                  <div key={label}>
                    <h4 className="text-xs font-bold text-[#9CA3AF] uppercase tracking-wider mb-2">{label}</h4>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                      <div><span className="text-[#9CA3AF]">Name:</span> <span className="text-[#1F2937]">{data.name}</span></div>
                      <div><span className="text-[#9CA3AF]">Designation:</span> <span className="text-[#1F2937]">{data.designation}</span></div>
                      <div><span className="text-[#9CA3AF]">Email:</span> <span className="text-[#1F2937]">{data.email}</span></div>
                      <div><span className="text-[#9CA3AF]">Mobile:</span> <span className="text-[#1F2937]">{data.mobile}</span></div>
                    </div>
                  </div>
                ))}

                {/* Authorized Representative */}
                <div>
                  <h4 className="text-xs font-bold text-[#9CA3AF] uppercase tracking-wider mb-2">Authorized Representative</h4>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                    <div><span className="text-[#9CA3AF]">Name:</span> <span className="text-[#1F2937]">{selected.authorized_representative?.name}</span></div>
                    <div><span className="text-[#9CA3AF]">Designation:</span> <span className="text-[#1F2937]">{selected.authorized_representative?.designation}</span></div>
                  </div>
                </div>

                {/* Agreements */}
                <div>
                  <h4 className="text-xs font-bold text-[#9CA3AF] uppercase tracking-wider mb-2">Agreements</h4>
                  <div className="space-y-3">
                    {['participation', 'safeguarding'].map((key) => {
                      const a = selected.agreements?.[key];
                      if (!a) return null;
                      const isPlaceholder = a.version?.includes('ABCD') || a.is_test_acceptance;
                      return (
                        <div key={key} className="flex items-center gap-2 text-sm flex-wrap">
                          <CheckCircle2 className={`h-4 w-4 shrink-0 ${isPlaceholder ? 'text-amber-400' : 'text-emerald-500'}`} />
                          <span className="text-[#1F2937]">{a.title || (key === 'participation' ? 'School Participation Agreement' : 'Safeguarding Responsibilities')}</span>
                          <Badge className={`border-0 text-[9px] ml-1 ${isPlaceholder ? 'bg-red-50 text-red-500' : 'bg-emerald-50 text-emerald-600'}`}>
                            {isPlaceholder ? 'Placeholder' : `v${a.version}`}
                          </Badge>
                          <span className="text-[#9CA3AF] text-xs">{new Date(a.accepted_at).toLocaleString()}</span>
                          {!isPlaceholder && (
                            <>
                              <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-[10px] text-blue-600 hover:bg-blue-50"
                                onClick={() => setViewAgreement({ key, record: a })}
                                data-testid={`view-agreement-${key}`}
                              >
                                View
                              </Button>
                              <a href={`/api/agreements/${key}/pdf?registration_id=${selected.id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center h-6 px-2 text-[10px] text-[#F97D6A] hover:bg-[#FFF0ED] rounded-md font-medium" data-testid={`download-agreement-${key}`}>
                                PDF
                              </a>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Changes Requested reason */}
                {selected.status === 'Changes Requested' && selected.changes_requested_reason && (
                  <div className="bg-orange-50 rounded-xl p-4">
                    <h4 className="text-xs font-bold text-orange-700 uppercase tracking-wider mb-1">Changes Requested</h4>
                    <p className="text-sm text-orange-800">{selected.changes_requested_reason}</p>
                    <p className="text-xs text-orange-500 mt-1">By {selected.changes_requested_by} on {new Date(selected.changes_requested_at).toLocaleString()}</p>
                  </div>
                )}

                {/* Actions */}
                {selected.status !== 'Approved' && (
                  <div className="border-t border-gray-100 pt-4 space-y-3">
                    <div className="flex gap-2">
                      <Button onClick={() => handleApprove(selected.id)} disabled={!!actionLoading} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm h-9 px-4" data-testid="approve-application-btn">
                        {actionLoading === 'approve' ? 'Approving...' : 'Approve Application'}
                      </Button>
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-[#6B7280] mb-1 block">Request Changes (enter reason)</Label>
                      <div className="flex gap-2">
                        <Input value={changesReason} onChange={(e) => setChangesReason(e.target.value)} placeholder="Reason for requesting changes..." className="rounded-xl h-9 text-sm border-gray-200 flex-1" data-testid="changes-reason-input" />
                        <Button variant="outline" onClick={() => handleRequestChanges(selected.id)} disabled={!!actionLoading} className="rounded-xl text-sm h-9 px-4 border-orange-200 text-orange-600 hover:bg-orange-50" data-testid="request-changes-btn">
                          {actionLoading === 'changes' ? 'Saving...' : 'Request Changes'}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Agreement Acceptance Link */}
                {selected.agreement_status !== 'accepted' && (
                  <div className="border-t border-gray-100 pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-[#1F2937]">Agreement Acceptance</p>
                        <p className="text-xs text-[#9CA3AF] mt-0.5">
                          {selected.agreement_link_sent_at ? `Link sent ${new Date(selected.agreement_link_sent_at).toLocaleString()}` : 'No acceptance link sent yet'}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs rounded-lg border-blue-200 text-blue-600 hover:bg-blue-50"
                        disabled={!!actionLoading}
                        data-testid="send-agreement-link-btn"
                        onClick={async () => {
                          setActionLoading('agreement');
                          try {
                            await api.post('/admin/send-agreement-link', { registration_id: selected.id });
                            toast.success('Agreement acceptance link sent');
                            onRefresh();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Failed');
                          }
                          setActionLoading('');
                        }}
                      >
                        {actionLoading === 'agreement' ? 'Sending...' : (selected.agreement_link_sent_at ? 'Resend Agreement Link' : 'Send Agreement Link')}
                      </Button>
                    </div>
                  </div>
                )}

                {selected.status === 'Approved' && (
                  <div className="bg-emerald-50 rounded-xl p-4 text-sm">
                    <p className="font-semibold text-emerald-700">School created: {selected.approved_school_id}</p>
                    <p className="text-emerald-600">Invite Code: {selected.approved_invite_code}</p>
                    <p className="text-xs text-emerald-500 mt-1">Approved by {selected.approved_by} on {new Date(selected.approved_at).toLocaleString()}</p>
                  </div>
                )}
              </div>

              <DialogFooter className="mt-5">
                <Button variant="outline" onClick={() => setSelected(null)} className="rounded-xl">Close</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Agreement Viewing Modal */}
      {viewAgreement && (
        <AgreementModal
          open={!!viewAgreement}
          onClose={() => setViewAgreement(null)}
          agreement={viewAgreement.key === 'participation' ? PARTICIPATION_AGREEMENT : SAFEGUARDING_AGREEMENT}
          acceptanceRecord={viewAgreement.record}
        />
      )}
    </div>
  );
}


// ─── Main Admin Dashboard ───
export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [schools, setSchools] = useState([]);
  const [coordinators, setCoordinators] = useState([]);
  const [careers, setCareers] = useState([]);
  const [careerRequests, setCareerRequests] = useState([]);
  const [demoRequests, setDemoRequests] = useState([]);
  const [schoolApplications, setSchoolApplications] = useState([]);
  const [activeTab, setActiveTab] = useState('schools');
  const [usersSchoolFilter, setUsersSchoolFilter] = useState('');
  const [interestNew, setInterestNew] = useState(0);

  const fetchAll = useCallback(async () => {
    try {
      const [statsRes, schoolsRes, coordsRes, careersRes, reqsRes, demoRes, appRes, interestRes] = await Promise.all([
        api.get('/stats'),
        api.get('/admin/schools'),
        api.get('/admin/coordinators'),
        api.get('/admin/careers'),
        api.get('/admin/career-requests'),
        api.get('/admin/demo-requests'),
        api.get('/admin/school-registrations'),
        api.get('/admin/school-interest'),
      ]);
      setStats(statsRes.data);
      setSchools(schoolsRes.data);
      setCoordinators(coordsRes.data);
      setCareers(careersRes.data);
      setCareerRequests(reqsRes.data);
      setDemoRequests(demoRes.data);
      setSchoolApplications(appRes.data);
      setInterestNew(interestRes.data?.new_count || 0);
    } catch (err) {
      console.error('Failed to fetch admin data', err);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Google consent returns here with ?google=connected|error
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('google');
    if (!result) return;
    setActiveTab('integrations');
    if (result === 'connected') toast.success('Google Calendar connected');
    else toast.error(params.get('reason') || 'Google authorization failed', { duration: 10000 });
    window.history.replaceState({}, '', '/admin');
  }, []);

  return (
    <div className="py-6 sm:py-8 min-h-screen bg-[#F5F6F8]" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1F2937] font-['Outfit']" data-testid="admin-title">
              Admin CMS
            </h1>
            <p className="text-sm text-[#4B5563] mt-1">Manage schools, coordinators, careers, and platform content</p>
          </div>
          <Button variant="outline" onClick={fetchAll} className="rounded-xl border-gray-200 text-sm" data-testid="refresh-data-btn">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>
        </div>

        <StatsBar stats={stats} />

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-2">
          <TabsList className="bg-white border border-gray-200 rounded-xl p-1 h-auto flex-wrap justify-start" data-testid="admin-tabs">
            <TabsTrigger value="school-applications" className="rounded-lg px-4 py-2 text-sm font-semibold data-[state=active]:bg-[#F97D6A] data-[state=active]:text-white" data-testid="tab-school-applications">
              <ClipboardList className="h-4 w-4 mr-1.5" /> School Applications ({schoolApplications.length})
            </TabsTrigger>
            <TabsTrigger value="schools" className="rounded-lg px-4 py-2 text-sm font-semibold data-[state=active]:bg-[#F97D6A] data-[state=active]:text-white" data-testid="tab-schools">
              <School className="h-4 w-4 mr-1.5" /> Schools ({schools.length})
            </TabsTrigger>
            <TabsTrigger value="coordinators" className="rounded-lg px-4 py-2 text-sm font-semibold data-[state=active]:bg-[#F97D6A] data-[state=active]:text-white" data-testid="tab-coordinators">
              <UserPlus className="h-4 w-4 mr-1.5" /> Coordinators ({coordinators.length})
            </TabsTrigger>
            <TabsTrigger value="users" className="rounded-lg px-4 py-2 text-sm font-semibold data-[state=active]:bg-[#F97D6A] data-[state=active]:text-white" data-testid="tab-users">
              <Users className="h-4 w-4 mr-1.5" /> Users
            </TabsTrigger>
            <TabsTrigger value="careers" className="rounded-lg px-4 py-2 text-sm font-semibold data-[state=active]:bg-[#F97D6A] data-[state=active]:text-white" data-testid="tab-careers">
              <GraduationCap className="h-4 w-4 mr-1.5" /> Careers ({careers.length})
            </TabsTrigger>
            <TabsTrigger value="requests" className="rounded-lg px-4 py-2 text-sm font-semibold data-[state=active]:bg-[#F97D6A] data-[state=active]:text-white" data-testid="tab-requests">
              <Lightbulb className="h-4 w-4 mr-1.5" /> Requests ({careerRequests.length})
            </TabsTrigger>
            <TabsTrigger value="internship-careers" className="rounded-lg px-4 py-2 text-sm font-semibold data-[state=active]:bg-[#F97D6A] data-[state=active]:text-white" data-testid="tab-internship-careers">
              <Briefcase className="h-4 w-4 mr-1.5" /> Internship Careers
            </TabsTrigger>
            <TabsTrigger value="demo-requests" className="rounded-lg px-4 py-2 text-sm font-semibold data-[state=active]:bg-[#F97D6A] data-[state=active]:text-white" data-testid="tab-demo-requests">
              <Phone className="h-4 w-4 mr-1.5" /> Demo Requests ({demoRequests.length})
            </TabsTrigger>
            <TabsTrigger value="integrations" className="rounded-lg px-4 py-2 text-sm font-semibold data-[state=active]:bg-[#F97D6A] data-[state=active]:text-white" data-testid="tab-integrations">
              <CalendarCheck className="h-4 w-4 mr-1.5" /> Integrations
            </TabsTrigger>
            <TabsTrigger value="tickets" className="rounded-lg px-4 py-2 text-sm font-semibold data-[state=active]:bg-[#F97D6A] data-[state=active]:text-white" data-testid="tab-tickets">
              <LifeBuoy className="h-4 w-4 mr-1.5" /> Tickets
            </TabsTrigger>
            <TabsTrigger value="school-interest" className="group rounded-lg px-4 py-2 text-sm font-semibold data-[state=active]:bg-[#F97D6A] data-[state=active]:text-white" data-testid="tab-school-interest">
              <HeartHandshake className="h-4 w-4 mr-1.5" /> School Interest
              {interestNew > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#F97D6A] text-white text-[10px] font-bold group-data-[state=active]:bg-white group-data-[state=active]:text-[#F97D6A]"
                      data-testid="school-interest-nav-count">
                  {interestNew}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="schools" className="mt-4">
            <SchoolsTab schools={schools} onRefresh={fetchAll} allCoordinators={coordinators}
                        onViewUsers={(sid) => { setUsersSchoolFilter(sid); setActiveTab('users'); }} />
          </TabsContent>
          <TabsContent value="coordinators" className="mt-4">
            <CoordinatorsTab coordinators={coordinators} schools={schools} onRefresh={fetchAll} />
          </TabsContent>
          <TabsContent value="users" className="mt-4">
            <UsersTab schools={schools} schoolFilter={usersSchoolFilter} onSchoolFilterChange={setUsersSchoolFilter} />
          </TabsContent>
          <TabsContent value="careers" className="mt-4">
            <CareersTab careers={careers} onRefresh={fetchAll} />
          </TabsContent>
          <TabsContent value="requests" className="mt-4">
            <CareerRequestsTab requests={careerRequests} onRefresh={fetchAll} />
          </TabsContent>
          <TabsContent value="internship-careers" className="mt-4">
            <InternshipCareerQueue />
          </TabsContent>
          <TabsContent value="demo-requests" className="mt-4">
            <DemoRequestsTab requests={demoRequests} onRefresh={fetchAll} />
          </TabsContent>
          <TabsContent value="school-applications" className="mt-4">
            <SchoolApplicationsTab applications={schoolApplications} onRefresh={fetchAll} />
          </TabsContent>
          <TabsContent value="integrations" className="mt-4">
            <GoogleCalendarCard />
          </TabsContent>
          <TabsContent value="tickets" className="mt-4">
            <TicketsTab schools={schools} />
          </TabsContent>
          <TabsContent value="school-interest" className="mt-4">
            <SchoolInterestTab onCountChange={setInterestNew} />
          </TabsContent>
        </Tabs>
      </div>
      <ReportIssueButton schools={schools} />
    </div>
  );
}
